<template>
  <div>
    <div class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black mb-8">
      <div class="bg-blue-50 p-6 rounded-t-lg">
        <div class="flex items-center mb-1">
          <div class="w-8 h-8 rounded-lg flex items-center justify-start">
            <LightBulbIcon class="w-6 h-6 text-blue-800" />
          </div>
          <h2 class="text-2xl font-semibold text-blue-900">AI Session Summary Generator</h2>
        </div>
        <p class="text-blue-800 font-semibold">
          Upload your notes or paste text to generate summaries, flashcards, and quiz questions.
        </p>
      </div>

      <div class="p-6">
        <!-- How it Works -->
        <div class="bg-blue-50 rounded-lg border border-blue-800 p-6 mb-6">
          <div class="flex items-center mb-3">
            <BoltIcon class="w-6 h-6 text-blue-800 mr-2" />
            <h2 class="text-lg font-bold text-blue-900">How it Works</h2>
          </div>
          <div class="space-y-2">
            <div class="flex items-center">
              <div
                class="w-[21px] h-[21px] bg-blue-600 text-white rounded-full flex items-center justify-center text-xs mr-3"
              >
                1
              </div>
              <p class="text-blue-800 font-semibold">
                Copy and paste your session notes in the text area below. Or drag and drop a single
                PDF, TXT, or DOCX file (max 10MB)
              </p>
            </div>
            <div class="flex items-center">
              <div
                class="w-[21px] h-[21px] bg-blue-600 text-white rounded-full flex items-center justify-center text-xs mr-3"
              >
                2
              </div>
              <p class="text-blue-800 font-semibold">
                Our AI will analyze your content and generate personalized study materials
              </p>
            </div>
          </div>
        </div>

        <!-- Upload Notes -->
        <div class="mb-6">
          <h3 class="font-semibold text-gray-900 mb-3">Upload Notes (PDF, TXT, DOCX)</h3>
          <div class="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <svg class="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-blue-800">
                  📁 Only <strong>1 file</strong> allowed at a time
                </p>
                <p class="text-xs text-blue-600 mt-1">
                  Maximum file size: 10MB • Supported formats: PDF, TXT, DOCX
                </p>
              </div>
            </div>
          </div>

          <div
            @click="triggerFileInput"
            @drop="handleFileDrop"
            @dragover="handleDragOver"
            @dragenter="handleDragEnter"
            @dragleave="handleDragLeave"
            :class="[
              'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
              isDragOver && !isFileUploadDisabled
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300',
              uploadedFiles.length > 0
                ? 'opacity-50 cursor-not-allowed'
                : isFileUploadDisabled
                ? 'opacity-50 cursor-not-allowed bg-gray-100'
                : 'cursor-pointer hover:border-gray-400',
            ]"
          >
            <div
              class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <ArrowUpTrayIcon class="w-8 h-8 text-blue-600" />
            </div>
            <p class="text-gray-900 font-semibold mb-2">
              {{
                uploadedFiles.length > 0
                  ? "Remove current file to upload another"
                  : isFileUploadDisabled
                  ? "Cannot upload files while notes are pasted below"
                  : "Click to upload file or drag and drop"
              }}
            </p>
            <p class="text-blue-600 text-sm">
              {{
                uploadedFiles.length > 0
                  ? "1/1 file uploaded"
                  : isFileUploadDisabled
                  ? "Clear the notes text below to enable file upload"
                  : "PDF, TXT, DOCX (Max 10MB, 1 file only)"
              }}
            </p>
          </div>

          <!-- Hidden file input -->
          <input
            ref="fileInputRef"
            type="file"
            accept=".pdf,.txt,.docx"
            @change="handleFileSelect"
            class="hidden"
          />

          <!-- Uploaded Files Display -->
          <div v-if="uploadedFiles.length > 0" class="mt-4">
            <h4 class="font-medium text-gray-900 mb-3 flex items-center">
              <svg class="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              Uploaded File (1/1)
            </h4>
            <div class="space-y-2">
              <div
                v-for="(file, index) in uploadedFiles"
                :key="index"
                class="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-4 shadow-sm"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center flex-1">
                    <div
                      class="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4"
                    >
                      <DocumentTextIcon class="w-6 h-6 text-green-600" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-semibold text-gray-900 truncate">{{ file.name }}</p>
                      <p class="text-xs text-gray-500 mt-1">
                        {{ formatFileSize(file.size) }} • {{ getFileTypeIcon(file.name) }}
                      </p>
                      <div class="flex items-center mt-2">
                        <div class="w-full bg-green-200 rounded-full h-2">
                          <div class="bg-green-500 h-2 rounded-full w-full"></div>
                        </div>
                        <span class="text-xs text-green-600 font-medium ml-2">Ready</span>
                      </div>
                    </div>
                  </div>
                  <button
                    @click="removeFile(index)"
                    class="flex-shrink-0 ml-4 p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors group cursor-pointer"
                    title="Remove file"
                  >
                    <XMarkIcon class="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Paste Notes -->
        <div class="mb-6">
          <h3 class="font-semibold text-gray-900 mb-3">Or Paste Your Notes</h3>
          <textarea
            ref="textareaRef"
            v-model="notesText"
            :disabled="isTextInputDisabled"
            maxlength="10000"
            :class="[
              'w-full min-h-[12rem] max-h-[20rem] p-4 text-gray-900 border border-gray-300 rounded-lg resize-none overflow-y-auto focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              isTextInputDisabled ? 'bg-gray-100 cursor-not-allowed opacity-90' : '',
            ]"
            :placeholder="
              isTextInputDisabled
                ? 'Cannot paste notes while files are uploaded above. Remove uploaded files to enable text input.'
                : 'Paste your session notes here for AI processing (minimum 2,000 characters)...'
            "
          ></textarea>
          <div class="flex justify-end mt-2">
            <span
              :class="[
                'text-sm',
                notesText.length < 2000
                  ? 'text-orange-600 font-medium'
                  : notesText.length > 9000
                  ? 'text-red-600'
                  : 'text-gray-500',
              ]"
            >
              {{ formatNumber(notesText.length) }}/{{ formatNumber(10000) }} characters
              <span v-if="notesText.length < 2000 && notesText.length > 0" class="ml-1">
                ({{ formatNumber(2000 - notesText.length) }} more needed)
              </span>
            </span>
          </div>
        </div>

        <!-- Content Type Selection -->
        <div class="mb-6">
          <h3 class="font-semibold text-gray-900 mb-3">Select Content Type to Generate</h3>
          <div class="flex justify-center space-x-8 max-w-full mx-auto">
            <button
              v-for="type in contentTypes"
              :key="type.id"
              @click="toggleContentType(type.id)"
              :class="[
                'flex-1 flex items-center justify-center text-center px-8 py-4 border-2 rounded-lg font-medium transition-all duration-200 min-w-[160px]',
                !isContentTypeSelectionEnabled
                  ? 'cursor-not-allowed opacity-50 bg-gray-200 border-gray-400 text-gray-500'
                  : isGenerationTypeBlocked(type.id)
                  ? 'cursor-not-allowed opacity-50 bg-gray-200 border-gray-400 text-gray-500'
                  : selectedType === type.id
                  ? `${type.selectedClasses} ${type.shadowClasses} cursor-pointer hover:translate-y-[-2px] hover:text-white`
                  : `${type.defaultClasses} ${type.shadowClasses} cursor-pointer hover:translate-y-[-2px] hover:text-white`,
              ]"
              :title="
                !isContentTypeSelectionEnabled
                  ? 'Upload a file or paste notes first'
                  : isGenerationTypeBlocked(type.id)
                  ? getBlockedReason(type.id)
                  : `Generate ${type.name}`
              "
            >
              <component :is="type.icon" class="w-5 h-5 mr-2" />
              {{ type.name }}
              <span v-if="!isContentTypeSelectionEnabled" class="ml-2 text-xs font-normal">
                (Disabled)
              </span>
              <span v-else-if="isGenerationTypeBlocked(type.id)" class="ml-2 text-xs font-normal">
                (Blocked)
              </span>
            </button>
          </div>
        </div>

        <!-- Generate Button -->
        <div class="text-center">
          <button
            @click="generateContent"
            :disabled="!isGenerateButtonEnabled || aiContentStore.isLoading"
            :class="[
              'relative overflow-hidden px-12 py-4 mt-10 mb-4 rounded-xl font-bold text-lg shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black transition-all duration-300 ease-in-out min-w-[280px] whitespace-nowrap',
              !isGenerateButtonEnabled || aiContentStore.isLoading
                ? 'cursor-not-allowed opacity-50 bg-gray-400 text-gray-200'
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-500 hover:to-purple-600 hover:transform hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] cursor-pointer before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700 before:ease-in-out',
            ]"
            :title="
              aiContentStore.isLoading
                ? 'AI content is being generated...'
                : !isGenerateButtonEnabled
                ? 'Please provide content and select a content type first'
                : 'Generate AI content based on your selection'
            "
            style="z-index: 10; position: relative"
          >
            <BoltIcon class="w-6 h-6 inline mr-3" />
            Generate AI Content
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Floating AI Content Notice - Appears at top center with subtle floating animation -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-500"
      enter-from-class="-translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition ease-in duration-300"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="-translate-y-full opacity-0"
    >
      <div
        v-if="showTextDisclaimer"
        class="fixed top-37 left-1/2 -translate-x-1/2 w-[95%] md:w-auto md:max-w-2xl floating-notice"
        style="z-index: 1000; pointer-events: none"
      >
        <div
          class="bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 border-2 border-amber-400 rounded-xl p-4 backdrop-blur-sm bg-opacity-95 shadow-lg"
          style="pointer-events: auto"
        >
          <button
            @click="showTextDisclaimer = false"
            class="absolute top-3 right-3 text-amber-700 hover:text-amber-900 hover:bg-amber-200 rounded-full p-1 transition-all duration-200"
          >
            <XMarkIcon class="w-5 h-5" />
          </button>
          <div class="flex items-start space-x-3 pr-8">
            <div class="flex-shrink-0 mt-0.5">
              <div
                class="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center animate-pulse"
              >
                <svg class="w-5 h-5 text-amber-900" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div class="text-sm text-amber-900 break-words flex-1">
              <strong class="font-bold text-base">AI Content Notice:</strong>
              <p class="mt-1">
                The AI-generated content in this app is for reference only and may contain errors.
                We encourage you to double-check the information and consult with your teachers or
                trusted sources. As of October 2025, this app uses the Google Gemini 2.5 Pro model
                to assist in processing notes. If you find anything unusual, please report it using
                the button or email us at
                <a
                  href="mailto:elevated.tutorapp@gmail.com"
                  class="underline hover:text-amber-800 font-semibold break-all"
                  >elevated.tutorapp@gmail.com</a
                >.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Floating Duplicate Content Warning - Similar to AI Content Notice -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-500"
      enter-from-class="-translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition ease-in duration-300"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="-translate-y-full opacity-0"
    >
      <div
        v-if="
          showDuplicateNotice &&
          isContentTypeSelectionEnabled &&
          (blockedGenerationTypes.summary || blockedGenerationTypes.flashcards)
        "
        class="fixed top-44 left-1/2 -translate-x-1/2 w-[95%] md:w-auto md:max-w-2xl floating-notice"
        style="z-index: 999; pointer-events: none"
      >
        <div
          class="bg-gradient-to-r from-orange-50 via-yellow-50 to-orange-50 border-2 border-orange-400 rounded-xl p-4 backdrop-blur-sm bg-opacity-95 shadow-lg"
          style="pointer-events: auto"
        >
          <button
            @click="dismissDuplicateNotice"
            class="absolute top-3 right-3 text-orange-700 hover:text-orange-900 hover:bg-orange-200 rounded-full p-1 transition-all duration-200"
          >
            <XMarkIcon class="w-5 h-5" />
          </button>
          <div class="flex items-start space-x-3 pr-8">
            <div class="flex-shrink-0 mt-0.5">
              <div
                class="w-8 h-8 bg-orange-400 rounded-full flex items-center justify-center animate-pulse"
              >
                <svg class="w-5 h-5 text-orange-900" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div class="text-sm text-orange-900 break-words flex-1">
              <strong class="font-bold text-base"
                >⚠️ Some generation types are blocked for this file:</strong
              >
              <ul class="mt-2 space-y-2">
                <li v-if="blockedGenerationTypes.summary" class="leading-relaxed">
                  <strong class="font-semibold">Summary:</strong>
                  {{ blockedGenerationTypes.summaryReason }}
                </li>
                <li v-if="blockedGenerationTypes.flashcards" class="leading-relaxed">
                  <strong class="font-semibold">Flashcards:</strong>
                  {{ blockedGenerationTypes.flashcardsReason }}
                </li>
              </ul>
              <p class="mt-3 text-xs text-orange-700 font-medium">
                💡 Tip: You can still generate the other type, or remove the existing material to
                unlock.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";
import { geminiService } from "@/services/geminiService";
import { useAIContentStore } from "@/stores/aiContentStore";
import { logDebug, logInfo, logWarn, logError, logSuccess } from "@/utils/secureLogger";
import {
  checkContentSafety,
  checkFileSafety,
  validateTextInputSafety,
  comprehensiveSecurityCheck,
} from "@/utils/contentSafetyFilter";
import { duplicateDetectionService } from "@/services/duplicateDetectionService";
import {
  validateAcademicContent,
  getValidationErrorMessage,
  ALLOWED_SUBJECTS,
} from "@/utils/academicContentValidator"; // ✅ Academic content validation
import { auth, getDoc, doc, db } from "@/firebase";
import {
  LightBulbIcon,
  BoltIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";

// Define props
interface Props {
  sessionContext?: {
    sessionId: string;
    subject: string;
  } | null;
}

const props = defineProps<Props>();

// Define emits for component events
const emit = defineEmits<{
  materialGenerated: [materialData: any];
}>();

onMounted(() => {
  document.title = "ElevatED – AI Generator";

  // If we have session context, log it for debugging
  if (props.sessionContext) {
    logInfo("AINoteProcessor loaded with session context", {
      sessionId: props.sessionContext.sessionId,
      subject: props.sessionContext.subject,
    });
  }
});

// Configure PDF.js worker - use static file from public directory
const setupPDFWorker = () => {
  try {
    // Check if we're in development or production
    const isDev = import.meta.env.DEV;
    const baseUrl = isDev ? window.location.origin : window.location.origin;

    // First try: Use the static worker file from public directory with absolute URL
    const workerUrl = `${baseUrl}/pdf.worker.min.js`;
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
    logDebug("PDF worker setup: Using static worker", { workerUrl });
  } catch (error) {
    logWarn("PDF worker setup: Static file failed, trying CDN fallback", error);
    try {
      // Fallback: Use unpkg CDN
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
      logDebug("PDF worker setup: Using unpkg CDN");
    } catch (cdnError) {
      logWarn("PDF worker setup: unpkg failed, trying cdnjs fallback", cdnError);
      try {
        // Fallback: Use cdnjs
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
        logDebug("PDF worker setup: Using cdnjs fallback");
      } catch (cdnError2) {
        logError("PDF worker setup: All methods failed", cdnError2);
        // Last resort: try jsdelivr
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
        logDebug("PDF worker setup: Using jsdelivr last resort");
      }
    }
  }
};

// Initialize PDF worker
setupPDFWorker();

// Reset PDF.js worker state function
const resetPDFWorker = () => {
  try {
    // Re-setup the worker to ensure clean state
    setupPDFWorker();
    logDebug("PDF worker reset successfully");
  } catch (error) {
    logWarn("PDF worker reset warning:", error);
  }
};

const router = useRouter();
const aiContentStore = useAIContentStore();

// Helper function to check if current user is a validator
const isValidator = async (): Promise<boolean> => {
  const currentUser = auth.currentUser;
  if (!currentUser) return false;

  try {
    const userDoc = await getDoc(doc(db, "users", currentUser.uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.userType === "validator";
    }
  } catch (error) {
    console.error("Error checking user role:", error);
  }
  return false;
};
const notesText = ref("");
const showTextDisclaimer = ref(false);
const selectedType = ref<string>("");
const uploadedFiles = ref<File[]>([]);
const isDragOver = ref(false);
const fileInputRef = ref<HTMLInputElement>();

// NEW: Track which generation types are blocked for uploaded file
const blockedGenerationTypes = ref<{
  summary: boolean;
  flashcards: boolean;
  summaryReason?: string;
  flashcardsReason?: string;
}>({
  summary: false,
  flashcards: false,
});

// Track if duplicate notice should be shown (can be dismissed)
const showDuplicateNotice = ref(true);

// ✅ Maximum retry attempts for generation (matches PreTestModal)
const MAX_RETRIES = 3;

// Rate limiting state for generation attempts
const failedGenerationAttempts = ref(0);
const lastGenerationAttemptTime = ref(0);

// Reset failed attempts on successful generation
const resetFailedAttempts = () => {
  failedGenerationAttempts.value = 0;
  lastGenerationAttemptTime.value = 0;
};

// Record failed generation attempt
const recordFailedGenerationAttempt = () => {
  // Increment attempts but never exceed MAX_RETRIES
  if (failedGenerationAttempts.value < MAX_RETRIES) {
    failedGenerationAttempts.value = failedGenerationAttempts.value + 1;
  } else {
    failedGenerationAttempts.value = MAX_RETRIES;
  }

  // Always update last attempt timestamp
  lastGenerationAttemptTime.value = Date.now();
};

// Computed properties for mutual exclusivity
const hasUploadedFiles = computed(() => uploadedFiles.value.length > 0);
const hasNotesText = computed(() => notesText.value.trim().length > 0);
const isFileUploadDisabled = computed(() => hasNotesText.value);
const isTextInputDisabled = computed(() => hasUploadedFiles.value);

// NEW: Progressive disclosure - Check if user has provided any content
const hasValidContent = computed(() => hasUploadedFiles.value || hasNotesText.value);

// NEW: Progressive disclosure - Check if content type selection should be enabled
const isContentTypeSelectionEnabled = computed(() => hasValidContent.value);

// NEW: Progressive disclosure - Check if generate button should be enabled
const isGenerateButtonEnabled = computed(() => hasValidContent.value && selectedType.value !== "");

// NEW: Check if a specific generation type is blocked
const isGenerationTypeBlocked = computed(() => {
  return (type: string) => {
    if (type === "summary") {
      return blockedGenerationTypes.value.summary;
    } else if (type === "flashcards") {
      return blockedGenerationTypes.value.flashcards;
    }
    return false;
  };
});

// NEW: Get reason for blocked generation type
const getBlockedReason = computed(() => {
  return (type: string) => {
    if (type === "summary") {
      return blockedGenerationTypes.value.summaryReason || "";
    } else if (type === "flashcards") {
      return blockedGenerationTypes.value.flashcardsReason || "";
    }
    return "";
  };
});

// Watch for notes text changes to show disclaimer AND check content safety
watch(
  () => notesText.value,
  (newValue) => {
    showTextDisclaimer.value = newValue.trim().length > 0;

    // ✅ ENHANCED SECURITY: Comprehensive security check (SQL injection, XSS, DDoS, profanity)
    if (newValue.trim().length > 0) {
      const securityCheck = comprehensiveSecurityCheck(newValue);

      if (!securityCheck.isSafe || securityCheck.blocked) {
        logWarn("⚠️ SECURITY THREAT DETECTED IN TEXT INPUT", {
          textLength: newValue.length,
          severity: securityCheck.severity,
          reasons: securityCheck.reasons,
          detectedTerms: securityCheck.detectedTerms?.slice(0, 3),
        });

        // Determine alert type based on severity
        const isCritical = securityCheck.severity === "critical";
        const isHighSeverity = securityCheck.severity === "high" || isCritical;

        // Immediately clear the unsafe content and show warning
        Swal.fire({
          icon: isHighSeverity ? "error" : "warning",
          title: isCritical
            ? "🚨 Security Threat Blocked"
            : isHighSeverity
            ? "⚠️ Suspicious Input Detected"
            : "Inappropriate Content Detected",
          html: `
            <div class="text-left space-y-3">
              ${
                isCritical
                  ? '<div class="bg-red-50 border-2 border-red-300 rounded-lg p-3 mb-3">' +
                    '<p class="text-red-800 font-bold mb-2">⚠️ CRITICAL SECURITY ALERT</p>' +
                    '<p class="text-red-700 text-sm">' +
                    securityCheck.message +
                    "</p>" +
                    "</div>"
                  : '<p class="mb-3 text-gray-700">' +
                    (securityCheck.message ||
                      securityCheck.reason ||
                      "The text you entered contains content that cannot be processed.") +
                    "</p>"
              }
              
              ${
                securityCheck.reasons && securityCheck.reasons.length > 0
                  ? '<div class="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-3">' +
                    '<p class="font-semibold text-sm mb-1">Security Issues Detected:</p>' +
                    '<ul class="text-sm text-gray-700 list-disc pl-5 space-y-1">' +
                    securityCheck.reasons.map((r) => "<li>" + r + "</li>").join("") +
                    "</ul>" +
                    "</div>"
                  : ""
              }
              
              <div class="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p class="font-semibold text-sm mb-2 text-blue-800">✅ ElevatED is a safe learning platform:</p>
                <ul class="text-sm text-blue-700 list-disc pl-5 space-y-1">
                  <li>Educational and age-appropriate content only</li>
                  <li>No profanity, explicit language, or violence</li>
                  <li>Protection against malicious code injection</li>
                  <li>Suitable for high school students</li>
                </ul>
              </div>
              
              <p class="text-sm text-gray-600 mt-3"><strong>The unsafe content has been automatically removed.</strong></p>
            </div>
          `,
          confirmButtonColor: isHighSeverity ? "#dc2626" : "#ef4444",
          confirmButtonText: "I Understand",
          allowOutsideClick: false,
          allowEscapeKey: false,
          width: "600px",
          customClass: {
            popup: "elevated-modal",
            title: "elevated-modal-title",
          },
        });

        // Clear the unsafe text
        notesText.value = "";
        showTextDisclaimer.value = false;
      }
    }
  }
);

const contentTypes = [
  {
    id: "summary",
    name: "Summary",
    icon: DocumentTextIcon,
    selectedClasses: "border-purple-700 text-white bg-purple-500",
    defaultClasses:
      "border-purple-700 text-purple-700 bg-white hover:bg-purple-500 hover:border-purple-600",
    shadowClasses: "shadow-[4px_4px_0_0_rgba(126,34,206,1)]", // purple-700
  },
  {
    id: "flashcards",
    name: "Flashcards",
    icon: DocumentDuplicateIcon,
    selectedClasses: "border-green-700 text-white bg-green-500",
    defaultClasses:
      "border-green-700 text-green-700 bg-white hover:bg-green-500 hover:border-green-600",
    shadowClasses: "shadow-[4px_4px_0_0_rgba(21,128,61,1)]", // green-700
  },
];

// NEW: Check filename existence in Firebase collections (type-specific)
const checkFilenameAvailability = async (filename: string) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId || !filename) {
      return;
    }

    logInfo("Checking filename availability for type-specific blocking", { filename });

    const filenameCheck = await duplicateDetectionService.checkFilenameExistsByType(
      userId,
      filename
    );

    // Update blocked states based on existence
    blockedGenerationTypes.value.summary = filenameCheck.existsInSummaries;
    blockedGenerationTypes.value.flashcards = filenameCheck.existsInFlashcards;

    if (filenameCheck.existsInSummaries) {
      blockedGenerationTypes.value.summaryReason = `You already have a summary from "${
        filenameCheck.summaryDoc!.filename
      }" (${duplicateDetectionService["formatDate"](filenameCheck.summaryDoc!.dateGenerated)})`;
      logWarn("Summary generation blocked for this filename", {
        filename,
        existingTopic: filenameCheck.summaryDoc!.topic,
      });
    }

    if (filenameCheck.existsInFlashcards) {
      blockedGenerationTypes.value.flashcardsReason = `You already have flashcards from "${
        filenameCheck.flashcardDoc!.filename
      }" (${duplicateDetectionService["formatDate"](filenameCheck.flashcardDoc!.dateGenerated)})`;
      logWarn("Flashcard generation blocked for this filename", {
        filename,
        existingTopic: filenameCheck.flashcardDoc!.topic,
      });
    }

    if (!filenameCheck.existsInSummaries && !filenameCheck.existsInFlashcards) {
      logSuccess("Filename available for both generation types", { filename });
    }
  } catch (error) {
    logError("Error checking filename availability", error);
    // Don't block on error - let the generation attempt proceed
  }
};

const toggleContentType = (typeId: string) => {
  // NEW: Prevent selection if no content provided yet
  if (!isContentTypeSelectionEnabled.value) {
    Swal.fire({
      icon: "info",
      title: "Upload or Paste Content First",
      html: "Please upload a file or paste your notes before selecting a content type.",
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "Got it",
    });
    return;
  }

  // NEW: Prevent selection if blocked
  if (isGenerationTypeBlocked.value(typeId)) {
    Swal.fire({
      icon: "warning",
      title: "Generation Type Blocked",
      html: `${getBlockedReason.value(
        typeId
      )}<br><br>Please remove the existing material or upload a different file.`,
      confirmButtonColor: "#f59e0b",
      confirmButtonText: "Got it",
    });
    return;
  }

  if (selectedType.value === typeId) {
    selectedType.value = ""; // Deselect if clicking the same button
  } else {
    selectedType.value = typeId; // Select the new type
  }
};

const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const getFileTypeIcon = (fileName: string): string => {
  const extension = fileName.toLowerCase().split(".").pop();
  switch (extension) {
    case "pdf":
      return "📄 PDF";
    case "txt":
      return "📝 Text";
    case "docx":
      return "📘 Word";
    default:
      return "📄 Document";
  }
};

/**
 * ✅ SECURITY: Verify file signature (magic bytes) matches file extension
 * Prevents malware disguised as PDF/DOCX/TXT (e.g., renamed .exe files)
 *
 * This checks the actual file content header, NOT just the filename extension
 * Reference: https://en.wikipedia.org/wiki/List_of_file_signatures
 */
const verifyFileSignature = async (
  file: File
): Promise<{ isValid: boolean; detectedType: string; reason?: string }> => {
  try {
    // Read first 8 bytes (enough to identify most file types)
    const blob = file.slice(0, 8);
    const arrayBuffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);

    // Convert bytes to hex string for logging
    const hexSignature = Array.from(bytes)
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join(" ")
      .toUpperCase();

    logDebug(`🔒 File signature check for ${file.name}`, {
      signature: hexSignature,
      fileType: file.type,
      fileName: file.name,
    });

    const fileName = file.name.toLowerCase();

    // ✅ PDF Validation: Must start with "%PDF-" (25 50 44 46 2D)
    if (fileName.endsWith(".pdf")) {
      const isPDF =
        bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;

      if (!isPDF) {
        logError(
          `❌ SECURITY ALERT: File ${file.name} claims to be PDF but has invalid signature`,
          {
            expected: "25 50 44 46 (PDF)",
            actual: hexSignature,
          }
        );
        return {
          isValid: false,
          detectedType: "unknown",
          reason: `This file is not a valid PDF. The file signature doesn't match PDF format. This could be a renamed executable or malicious file.`,
        };
      }

      logSuccess(`✅ PDF signature verified for ${file.name}`);
      return { isValid: true, detectedType: "pdf" };
    }

    // ✅ DOCX Validation: Must start with PK (50 4B) - ZIP archive signature
    // DOCX is a ZIP archive containing XML files
    if (fileName.endsWith(".docx")) {
      const isZIP = bytes[0] === 0x50 && bytes[1] === 0x4b;

      if (!isZIP) {
        logError(
          `❌ SECURITY ALERT: File ${file.name} claims to be DOCX but has invalid signature`,
          {
            expected: "50 4B (ZIP/DOCX)",
            actual: hexSignature,
          }
        );
        return {
          isValid: false,
          detectedType: "unknown",
          reason: `This file is not a valid DOCX. The file signature doesn't match Microsoft Word format. This could be a renamed executable or malicious file.`,
        };
      }

      // Additional check: DOCX should have specific ZIP entries
      // We'll do a deeper validation when extracting content
      logSuccess(`✅ DOCX signature (ZIP) verified for ${file.name}`);
      return { isValid: true, detectedType: "docx" };
    }

    // ✅ TXT Validation: Plain text files (no specific signature, just validate it's not executable)
    if (fileName.endsWith(".txt")) {
      // Check for common executable signatures
      const isExecutable =
        (bytes[0] === 0x4d && bytes[1] === 0x5a) || // MZ (Windows .exe)
        (bytes[0] === 0x7f && bytes[1] === 0x45 && bytes[2] === 0x4c && bytes[3] === 0x46) || // ELF (Linux executable)
        (bytes[0] === 0xcf && bytes[1] === 0xfa && bytes[2] === 0xed && bytes[3] === 0xfe) || // Mach-O (macOS executable)
        (bytes[0] === 0x50 && bytes[1] === 0x45 && bytes[2] === 0x00 && bytes[3] === 0x00); // PE (Windows .exe alternative)

      if (isExecutable) {
        logError(`❌ SECURITY ALERT: File ${file.name} claims to be TXT but is an EXECUTABLE`, {
          expected: "Plain text",
          actual: hexSignature,
          threat: "EXECUTABLE FILE DETECTED",
        });
        return {
          isValid: false,
          detectedType: "executable",
          reason: `SECURITY THREAT DETECTED: This file appears to be an executable program disguised as a text file. Upload blocked for your protection.`,
        };
      }

      logSuccess(`✅ TXT file verified (not executable) for ${file.name}`);
      return { isValid: true, detectedType: "txt" };
    }

    // Should never reach here if file extension validation passed
    return { isValid: false, detectedType: "unknown", reason: "Unsupported file type" };
  } catch (error) {
    logError(`Failed to verify file signature for ${file.name}`, error);
    return {
      isValid: false,
      detectedType: "error",
      reason: "Unable to verify file integrity. Please try again or use a different file.",
    };
  }
};

/**
 * ✅ SECURITY: Verify MIME type matches expected file type
 * Additional layer of validation beyond file signature
 */
const verifyMIMEType = (file: File): { isValid: boolean; reason?: string } => {
  const fileName = file.name.toLowerCase();
  const mimeType = file.type.toLowerCase();

  logDebug(`🔒 MIME type check for ${file.name}`, {
    reportedMIME: mimeType || "(empty)",
    fileName: file.name,
  });

  // PDF should have MIME type application/pdf
  if (fileName.endsWith(".pdf")) {
    if (mimeType !== "application/pdf" && mimeType !== "") {
      logWarn(`⚠️ PDF MIME type mismatch for ${file.name}`, {
        expected: "application/pdf",
        actual: mimeType,
      });
      // Warning only - some systems don't set MIME correctly
    }
    return { isValid: true };
  }

  // DOCX should have MIME type application/vnd.openxmlformats-officedocument.wordprocessingml.document
  if (fileName.endsWith(".docx")) {
    const validDocxMimes = [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/zip", // Sometimes reported as ZIP
      "", // Empty is acceptable (not all systems set MIME)
    ];

    if (!validDocxMimes.includes(mimeType)) {
      logWarn(`⚠️ DOCX MIME type unexpected for ${file.name}`, {
        expected: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        actual: mimeType,
      });
      // Warning only
    }
    return { isValid: true };
  }

  // TXT should have MIME type text/plain
  if (fileName.endsWith(".txt")) {
    // Block if MIME suggests executable
    const dangerousMimes = [
      "application/x-msdownload", // .exe
      "application/x-executable",
      "application/x-elf",
      "application/x-mach-binary",
    ];

    if (dangerousMimes.includes(mimeType)) {
      logError(`❌ SECURITY ALERT: TXT file has executable MIME type`, {
        fileName: file.name,
        mimeType: mimeType,
      });
      return {
        isValid: false,
        reason: "This file reports as an executable program. Upload blocked for security.",
      };
    }

    return { isValid: true };
  }

  return { isValid: true };
};

const isValidFileType = (file: File): boolean => {
  const allowedTypes = [".pdf", ".txt", ".docx"];
  const fileName = file.name.toLowerCase();
  return allowedTypes.some((type) => fileName.endsWith(type));
};

const isValidFileSize = (file: File): boolean => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  return file.size <= maxSize;
};

/**
 * ✅ SECURITY: Detect corrupted files by checking file structure integrity
 * Prevents processing of corrupted/malformed files that passed signature check
 */
const detectCorruptedFile = async (
  file: File
): Promise<{ isCorrupted: boolean; reason?: string }> => {
  try {
    const fileName = file.name.toLowerCase();

    // Check if file size is suspiciously small (likely corrupted or empty)
    if (file.size < 10) {
      return {
        isCorrupted: true,
        reason: "File size too small (less than 10 bytes). File may be corrupted or empty.",
      };
    }

    // Check if file size is exactly 0 (empty file)
    if (file.size === 0) {
      return {
        isCorrupted: true,
        reason: "File is empty (0 bytes). Cannot process empty files.",
      };
    }

    // PDF corruption detection (LENIENT - allow complex scientific PDFs)
    if (fileName.endsWith(".pdf")) {
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);

      // Check PDF header (must start with %PDF)
      const pdfHeader = String.fromCharCode(...bytes.slice(0, 4));
      if (pdfHeader !== "%PDF") {
        return {
          isCorrupted: true,
          reason: "PDF header missing or corrupted. File is not a valid PDF.",
        };
      }

      // LENIENT: Check for PDF EOF marker (should end with %%EOF)
      // Some PDFs may have trailing data, so check last 100 bytes instead of just 10
      const lastBytes = String.fromCharCode(...bytes.slice(-100));
      if (!lastBytes.includes("%%EOF")) {
        // LENIENT: Don't immediately reject - log warning and allow through
        logWarn(`PDF EOF marker not found in ${file.name} - may be incomplete, attempting anyway`);
        // Don't return corrupted - let PDF.js library handle it
      }

      // LENIENT: Check for minimum PDF structure elements (catalog and pages)
      // Use non-fatal decoding to handle PDFs with complex math symbols/Unicode
      const pdfContent = new TextDecoder("utf-8", { fatal: false }).decode(arrayBuffer);
      const hasCatalog = pdfContent.includes("/Catalog");
      const hasPages = pdfContent.includes("/Pages");

      if (!hasCatalog || !hasPages) {
        // LENIENT: Some PDFs may have obfuscated structure - log warning but allow
        logWarn(
          `PDF structure check failed for ${file.name} - missing catalog/pages, attempting anyway`
        );
        // Don't return corrupted - let PDF.js library handle it during extraction
      }
    }

    // DOCX corruption detection
    if (fileName.endsWith(".docx")) {
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);

      // Check ZIP header (DOCX is a ZIP archive)
      const zipHeader = String.fromCharCode(...bytes.slice(0, 2));
      if (zipHeader !== "PK") {
        return {
          isCorrupted: true,
          reason: "DOCX header corrupted. File is not a valid ZIP archive.",
        };
      }

      // Check for minimum DOCX structure (should contain word/ directory)
      const docxContent = new TextDecoder("utf-8", { fatal: false }).decode(arrayBuffer);
      if (!docxContent.includes("word/document.xml")) {
        return {
          isCorrupted: true,
          reason: "DOCX structure corrupted. Missing required document.xml file.",
        };
      }
    }

    // TXT corruption detection (LENIENT - allow math/science symbols and Unicode)
    if (fileName.endsWith(".txt")) {
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);

      // Only check for EXTREME null byte corruption (>80% null bytes = truly corrupted)
      // Reduced from 30% to 80% to allow Unicode, math symbols, and special characters
      let nullByteCount = 0;
      for (let i = 0; i < Math.min(bytes.length, 1000); i++) {
        if (bytes[i] === 0) nullByteCount++;
      }

      const nullByteRatio = nullByteCount / Math.min(bytes.length, 1000);
      if (nullByteRatio > 0.8) {
        return {
          isCorrupted: true,
          reason: "Text file contains excessive null bytes. File may be corrupted or binary data.",
        };
      }

      // Try to decode as UTF-8 (NON-FATAL - allow encoding variations for international content)
      // Changed fatal: true → fatal: false to accept math symbols, emojis, and special characters
      try {
        const decoder = new TextDecoder("utf-8", { fatal: false }); // LENIENT decoding
        const text = decoder.decode(arrayBuffer);

        // Only reject if the decoded text is completely empty or just whitespace
        if (text.trim().length === 0) {
          return {
            isCorrupted: true,
            reason: "Text file is empty or contains no readable content.",
          };
        }
      } catch (error) {
        // Even with fatal: false, if it fails, just log it but don't reject
        logWarn(`TXT decoding warning for ${file.name}:`, error);
        // Don't reject - let the extraction function handle it
      }
    }

    return { isCorrupted: false };
  } catch (error: any) {
    // LENIENT: Don't auto-reject on processing errors - log and allow file through
    logWarn(`Corruption detection encountered error for ${file.name}:`, error);
    // Return NOT corrupted - let the extraction function determine if it's truly bad
    return { isCorrupted: false };
  }
};

// File content extraction functions
const extractTextFromPDF = async (file: File): Promise<string> => {
  logDebug("Starting PDF text extraction", { filename: file.name, size: file.size });

  try {
    // Reset PDF worker state to ensure clean processing
    resetPDFWorker();
    logDebug("PDF worker reset completed");

    const arrayBuffer = await file.arrayBuffer();
    logDebug(`PDF arrayBuffer loaded, size: ${arrayBuffer.byteLength} bytes`);

    // LENIENT: Enhanced PDF loading options with multiple fallback strategies
    const pdfOptions = {
      data: arrayBuffer,
      verbosity: 0, // Reduce PDF.js logging
      disableFontFace: true, // Disable font loading to avoid issues
      disableAutoFetch: false,
      disableStream: false,
      disableRange: false,
      useWorkerFetch: false, // Disable worker fetch to avoid module loading issues
      isEvalSupported: false, // Disable eval for security and compatibility
    };

    const loadingTask = pdfjsLib.getDocument(pdfOptions);

    let pdf;
    try {
      logDebug(`Attempting to load PDF document...`);
      pdf = await loadingTask.promise;
      logDebug(`PDF loaded successfully, pages: ${pdf.numPages}`);
    } catch (loadError) {
      logWarn("PDF loading failed on first attempt:", loadError);

      // Check if it's a worker-related error and try to fix it
      if (
        loadError instanceof Error &&
        (loadError.message.includes("worker") ||
          loadError.message.includes("fetch") ||
          loadError.message.includes("import"))
      ) {
        logDebug("Detected worker error, attempting worker reset...");
        resetPDFWorker();

        // Wait a bit for worker to initialize
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // LENIENT: Try with more conservative settings (Attempt 2)
      logDebug("Retrying PDF load with conservative settings...");
      try {
        const conservativeLoadingTask = pdfjsLib.getDocument({
          data: arrayBuffer,
          verbosity: 0,
          disableFontFace: true,
          disableAutoFetch: true,
          disableStream: true,
          disableRange: true,
          useWorkerFetch: false,
          isEvalSupported: false,
        });

        pdf = await conservativeLoadingTask.promise;
        logDebug(`PDF loaded successfully on retry, pages: ${pdf.numPages}`);
      } catch (retryError) {
        logWarn("PDF loading failed on second attempt:", retryError);

        // LENIENT: Try with maximum compatibility settings (Attempt 3)
        logDebug("Attempting PDF load with maximum compatibility settings...");
        const maxCompatLoadingTask = pdfjsLib.getDocument({
          data: arrayBuffer,
          verbosity: 0,
          disableFontFace: true,
          disableAutoFetch: true,
          disableStream: true,
          disableRange: true,
          useWorkerFetch: false,
          isEvalSupported: false,
          pdfBug: false,
          standardFontDataUrl: undefined, // Don't load standard fonts
        });

        pdf = await maxCompatLoadingTask.promise;
        logDebug(`PDF loaded successfully with maximum compatibility, pages: ${pdf.numPages}`);
      }
    }

    let fullText = "";
    let blankPageCount = 0;
    const totalPages = pdf.numPages;

    logDebug(`Starting page-by-page text extraction for ${totalPages} pages...`);

    for (let i = 1; i <= totalPages; i++) {
      try {
        logDebug(`Processing page ${i}...`);
        const page = await pdf.getPage(i);
        logDebug(`Page ${i} loaded successfully`);

        const textContent = await page.getTextContent();
        logDebug(`Text content extracted, items count:`, {
          pageNumber: i,
          itemsCount: textContent.items.length,
        });

        logDebug(`Starting text extraction for page ${i}...`);
        // Better text extraction - handle different item types and spacing
        let pageText = "";

        // First attempt: standard text extraction
        try {
          pageText = textContent.items
            .map((item: any) => {
              // Handle different PDF.js text item structures
              if (item && typeof item === "object") {
                if (item.str && typeof item.str === "string") {
                  return item.str;
                }
                // Some PDFs might have different property names
                if (item.text && typeof item.text === "string") {
                  return item.text;
                }
              }
              return "";
            })
            .filter((str) => str && str.trim().length > 0)
            .join(" ");
          logDebug(`Standard extraction successful for page ${i}`);
        } catch (textExtractionError) {
          logWarn(`Error in text extraction for page ${i}:`, textExtractionError);

          // Fallback: try simpler extraction
          try {
            pageText = textContent.items
              .filter((item: any) => item && item.str)
              .map((item: any) => item.str)
              .join(" ");
            logDebug(`Fallback extraction successful for page ${i}`);
          } catch (fallbackError) {
            logWarn(`Fallback extraction failed for page ${i}:`, fallbackError);
            pageText = "";
          }
        }

        logDebug(`Page ${i} extracted text length: ${pageText.length}`);
        if (pageText.length > 0) {
          logDebug(`Page ${i} text preview:`, { preview: pageText.substring(0, 100) + "..." });
        } else {
          logDebug(`Page ${i} - No text extracted (may contain images/diagrams only)`);
        }

        // LENIENT: Consider a page blank only if it's TRULY empty (<5 chars)
        // Reduced from 30 to 5 to allow pages with formulas, symbols, or minimal text
        if (pageText.trim().length < 5) {
          logDebug(`Page ${i} considered blank - length: ${pageText.trim().length}`);
          blankPageCount++;
        }

        fullText += pageText + " ";
      } catch (pageError) {
        logWarn(`Error processing page ${i}:`, pageError);
        // LENIENT: Don't count failed pages as blank - they might have valid content
        // Just log and continue
      }
    }

    logDebug(`Page processing completed. Total text length: ${fullText.length}`);

    // Clean up the PDF document
    pdf.destroy();
    logDebug(`PDF document destroyed/cleaned up`);

    // LENIENT: Check if more than 95% of pages are blank (increased from 80%)
    // Science PDFs with diagrams, formulas, and images should now pass
    const blankPagePercentage = (blankPageCount / totalPages) * 100;
    logDebug(
      `Blank page analysis - ${blankPagePercentage}% (${blankPageCount}/${totalPages} pages)`
    );

    if (blankPagePercentage > 95) {
      logDebug(`Too many blank pages detected (>${blankPagePercentage}%)`);
      throw new Error(
        "REJECT: More than 95% of your PDF pages are blank or contain only images with no extractable text. Please upload a text-based document."
      );
    }

    const extractedText = fullText.trim();
    logDebug(`Final extracted text length: ${extractedText.length}`);
    if (extractedText.length > 0) {
      logDebug(`Final text preview:`, { preview: extractedText.substring(0, 200) + "..." });
    }

    // LENIENT: Additional check for completely empty content
    if (extractedText.length === 0) {
      logDebug(`No text content extracted`);
      throw new Error(
        "REJECT: No text content could be extracted from this PDF. The file may contain only images or be corrupted."
      );
    }

    // LENIENT: Check if we got mostly whitespace or very short content
    // Reduced minimum from 10 to 3 chars to allow formula-only pages like "E=mc²"
    if (extractedText.replace(/\s+/g, " ").trim().length < 3) {
      logDebug(`Insufficient readable text`);
      throw new Error(
        "REJECT: PDF contains insufficient readable text content. The file may be mostly images or poorly formatted."
      );
    }

    logSuccess(`PDF extraction completed successfully!`);
    return extractedText;
  } catch (error: unknown) {
    logError("PDF extraction error:", error);

    // Log more detailed error information
    if (error instanceof Error) {
      logError("Error details:", {
        name: error.name,
        message: error.message,
        stack: import.meta.env.DEV ? error.stack : undefined,
      });
    }

    // If it's already a REJECT error, pass it through
    if (error instanceof Error && error.message.includes("REJECT:")) {
      logDebug("Passing through REJECT error:", { message: error.message });
      throw error;
    }

    // LENIENT: Provide more specific error message based on error type
    // Only reject on critical errors (password, truly corrupted, etc.)
    let errorMessage = "REJECT: Failed to extract text from PDF - ";
    if (error instanceof Error) {
      // Critical errors that should block
      if (error.message.includes("password") || error.message.includes("encrypted")) {
        errorMessage += "the file is password-protected. Please remove the password and try again.";
      } else if (
        error.message.includes("Invalid PDF structure") ||
        error.message.includes("PDF header")
      ) {
        errorMessage += "the file appears to be corrupted or not a valid PDF.";
      }
      // Less critical errors - provide helpful message but still try to process
      else if (error.message.includes("XRef")) {
        errorMessage +=
          "the file structure has issues. Try re-saving the PDF or converting it to a new PDF file.";
      } else if (error.message.includes("Worker") || error.message.includes("worker")) {
        errorMessage +=
          "PDF processing service encountered an issue. Please try refreshing the page and uploading again.";
      } else if (error.message.includes("fetch") || error.message.includes("import")) {
        errorMessage +=
          "PDF processing service failed to load. Please check your internet connection and try again.";
      } else if (error.message.includes("Setting up fake worker failed")) {
        errorMessage +=
          "PDF processing initialization failed. Please refresh the page and try again.";
      } else {
        // LENIENT: For unknown errors, provide helpful message
        errorMessage += `${error.message}. Try re-saving the PDF or converting to a different format.`;
      }
    } else {
      errorMessage +=
        "unknown error occurred. Please try re-saving the PDF or converting to a different format.";
    }

    logError("Final error message:", { errorMessage });
    throw new Error(errorMessage);
  }
};

const extractTextFromTXT = async (file: File): Promise<string> => {
  try {
    logDebug(`Starting TXT extraction for: ${file.name}`);
    const text = await file.text();
    const trimmedText = text.trim();
    logDebug(`TXT extracted text length: ${trimmedText.length}`);

    if (trimmedText.length === 0) {
      throw new Error("REJECT: The text file appears to be empty or contains no readable content.");
    }

    return trimmedText;
  } catch (error: unknown) {
    logError("TXT extraction error:", error);
    if (error instanceof Error && error.message.includes("REJECT:")) {
      throw error;
    }
    throw new Error(
      "REJECT: The text file could not be read. This usually means the file is corrupted or was saved in a non-standard text format. Try re-saving your text as a plain .txt file with UTF-8 encoding."
    );
  }
};

const extractTextFromDOCX = async (file: File): Promise<string> => {
  try {
    logDebug(`Starting DOCX extraction for: ${file.name}`);
    const arrayBuffer = await file.arrayBuffer();
    logDebug(`DOCX arrayBuffer loaded, size: ${arrayBuffer.byteLength} bytes`);

    // ✅ SECURITY: Verify it's a valid Office Open XML document
    // DOCX files should contain specific entries like [Content_Types].xml
    try {
      // Mammoth will throw an error if it's not a valid DOCX/ZIP structure
      const result = await mammoth.extractRawText({ arrayBuffer });
      const extractedText = result.value.trim();
      logDebug(`DOCX extracted text length: ${extractedText.length}`);

      if (extractedText.length === 0) {
        throw new Error(
          "REJECT: No readable text found in this Word document. The file either contains only images/diagrams with no text, is completely blank, or the text extraction failed due to document corruption."
        );
      }

      logSuccess(`✅ DOCX file validated and text extracted: ${extractedText.length} characters`);
      return extractedText;
    } catch (mammothError: any) {
      // If mammoth fails, it's likely not a valid DOCX (could be malicious ZIP)
      logError(`Mammoth extraction failed - file may not be valid DOCX:`, mammothError);

      // Check if it's a parsing error (indicates invalid DOCX structure)
      if (
        mammothError.message?.includes("End of data reached") ||
        mammothError.message?.includes("Invalid") ||
        mammothError.message?.includes("Cannot read")
      ) {
        throw new Error(
          "REJECT: This file has an invalid Microsoft Word document structure. It claims to be a .docx file but cannot be opened as one. This could mean: (1) The file extension was manually changed from another format, (2) The document was corrupted during download or transfer, or (3) It's an older .doc format that needs to be re-saved as .docx."
        );
      }

      throw mammothError; // Re-throw if it's a different error
    }
  } catch (error: unknown) {
    logError("DOCX extraction error:", error);
    if (error instanceof Error && error.message.includes("REJECT:")) {
      throw error;
    }
    throw new Error(
      "REJECT: The Word document could not be processed. This typically happens when: (1) The file is corrupted or incomplete, (2) It was created in an older Word version and needs to be re-saved as .docx, or (3) The document has internal formatting errors. Try opening and re-saving the document in Microsoft Word or converting it to PDF."
    );
  }
};

// Validation functions
const isGibberish = (text: string): boolean => {
  // Check for excessive special characters or random patterns
  const specialCharRatio = (text.match(/[^a-zA-Z0-9\s]/g) || []).length / text.length;

  // Check for patterns that indicate gibberish: mostly numbers or special chars only
  const mostlyNumbers = /^\s*[\d\W]+\s*$/.test(text);

  // Improved word recognition - count meaningful words (including contractions and hyphenated words)
  const words = text.trim().split(/\s+/);
  const meaningfulWords = words.filter((word) => {
    // Remove common punctuation from word ends for better recognition
    const cleanWord = word.replace(/^[^\w]+|[^\w]+$/g, "");
    // Accept words that are mostly alphabetic (allowing apostrophes, hyphens)
    return cleanWord.length >= 2 && /^[a-zA-Z][a-zA-Z'-]*[a-zA-Z]$|^[a-zA-Z]$/.test(cleanWord);
  });
  const wordRatio = words.length > 0 ? meaningfulWords.length / words.length : 0;

  // ✅ ACADEMIC CONTENT FRIENDLY: Check for legitimate mathematical/scientific notation
  // Look for common academic indicators that should NOT be flagged as gibberish
  const hasAcademicNotation =
    /\b(sin|cos|tan|log|ln|sqrt|integral|derivative|equation|formula|theorem)\b/i.test(text) ||
    /[°²³√∫∑∏πθαβγδεω]/g.test(text) || // Common math/Greek symbols
    /\d+\s*[+\-*/=<>]\s*\d+/g.test(text) || // Mathematical expressions
    /[A-Za-z]\s*=\s*[^=]/g.test(text); // Variable assignments (e.g., "x = 5")

  logDebug(
    `Gibberish check - Special char ratio: ${specialCharRatio.toFixed(
      3
    )}, Word ratio: ${wordRatio.toFixed(
      3
    )}, Mostly numbers: ${mostlyNumbers}, Has academic notation: ${hasAcademicNotation}`
  );
  logDebug(`Total words: ${words.length}, Meaningful words: ${meaningfulWords.length}`);

  // ✅ IMPROVED LOGIC: Only reject if MULTIPLE indicators suggest gibberish
  // Academic content with math formulas should pass if word ratio is decent

  // Strong rejection criteria (definitely gibberish)
  if (mostlyNumbers) {
    logDebug("❌ Rejected: Content is mostly numbers/symbols only");
    return true;
  }

  if (wordRatio < 0.2) {
    logDebug("❌ Rejected: Very low word ratio (<20%)");
    return true;
  }

  // Moderate rejection criteria (only if no academic indicators present)
  if (specialCharRatio > 0.7 && !hasAcademicNotation) {
    logDebug("❌ Rejected: Excessive special characters (>70%) without academic context");
    return true;
  }

  // ✅ PASS: Content has decent word ratio (≥50%) - likely legitimate academic content
  if (wordRatio >= 0.5) {
    logDebug("✅ Passed: Good word ratio (≥50%) - likely legitimate content");
    return false;
  }

  // ✅ PASS: Content has academic notation - likely legitimate educational material
  if (hasAcademicNotation) {
    logDebug("✅ Passed: Academic notation detected - likely educational content");
    return false;
  }

  // Edge case: Low word ratio (20-50%) without academic indicators - likely gibberish
  logDebug("❌ Rejected: Low word ratio without academic indicators");
  return true;
};

const validateFileContent = async (
  file: File
): Promise<{
  isValid: boolean;
  message: string;
  text?: string;
  duplicateInfo?: { isDuplicate: boolean; type?: string; message?: string };
}> => {
  // Create a unique validation session identifier for logging
  const validationId = `${file.name}_${Date.now()}`;
  logDebug(`Starting validation for file: ${file.name}, size: ${file.size} bytes`, {
    validationId,
  });

  try {
    let extractedText = "";
    const fileExtension = file.name.toLowerCase().split(".").pop();

    logDebug(`File extension detected: ${fileExtension}`, { validationId });

    // Extract text based on file type - each extraction is independent
    switch (fileExtension) {
      case "pdf":
        logDebug(`Processing PDF file...`, { validationId });
        extractedText = await extractTextFromPDF(file);
        break;
      case "txt":
        logDebug(`Processing TXT file...`, { validationId });
        extractedText = await extractTextFromTXT(file);
        break;
      case "docx":
        logDebug(`Processing DOCX file...`, { validationId });
        extractedText = await extractTextFromDOCX(file);
        break;
      default:
        logDebug(`Unsupported file type: ${fileExtension}`, { validationId });
        return { isValid: false, message: "REJECT: Unsupported file type" };
    }

    logDebug(`Extracted text length: ${extractedText.length} characters`, { validationId });

    // Debug: Log first 200 characters of extracted text
    logDebug(`Text preview:`, {
      validationId,
      preview: extractedText.substring(0, 200) + "...",
    });

    // CRITICAL: CONTENT SAFETY CHECK - Block inappropriate content in files
    logDebug(`Performing content safety check on file`, { validationId });
    const safetyCheck = await checkFileSafety(extractedText, file.name);

    if (safetyCheck.blocked) {
      logError(`File blocked due to inappropriate content`, {
        validationId,
        severity: safetyCheck.severity,
        reasonCount: safetyCheck.reasons.length,
      });
      return {
        isValid: false,
        message: `REJECT: Inappropriate content detected in your file. Our content filter found: ${safetyCheck.reasons.join(
          ", "
        )}. This system can only process educational content that is free from explicit language, profanity, violence, or adult themes. Please upload appropriate academic material.`,
      };
    }

    if (safetyCheck.severity === "medium") {
      logWarn(`File contains potentially inappropriate language`, {
        validationId,
        severity: safetyCheck.severity,
        reasons: safetyCheck.reasons,
      });
    }

    // Fresh validation checks for this specific file
    if (extractedText.length > 10000) {
      logDebug(`Validation failed: Text too long`, { validationId });
      return {
        isValid: false,
        message: `REJECT: Your file contains ${extractedText.length.toLocaleString()} characters, which exceeds the maximum limit of 10,000 characters. This limit ensures quality AI processing and reasonable generation times. Please split your content into smaller sections or summarize the key points before uploading.`,
      };
    }

    // ✅ CONSISTENCY FIX: Accept files with exactly 2,000 characters or more
    if (extractedText.length <= 1999) {
      logDebug(`Validation failed: Text too short`, { validationId });
      return {
        isValid: false,
        message: `REJECT: Your file contains only ${extractedText.length.toLocaleString()} characters. For quality AI-generated summaries and flashcards, we require at least 2,000 characters of meaningful educational content. Please upload a more substantial document.`,
      };
    }

    const gibberishCheck = isGibberish(extractedText);
    logDebug(`Gibberish check result: ${gibberishCheck}`, { validationId });

    if (gibberishCheck) {
      logDebug(`Validation failed: Content appears to be gibberish`, { validationId });
      return {
        isValid: false,
        message:
          "REJECT: The file content appears to be unreadable. Our analysis detected mostly random characters, excessive symbols, or nonsensical text patterns that cannot be processed for educational content generation. This could mean: (1) The file encoding is incorrect, (2) The file was corrupted, or (3) The content is not in a readable language format. Please check your file and try again.",
      };
    }

    // CRITICAL: DUPLICATE DETECTION CHECK - Allow upload but block specific generation type
    logDebug(`Performing duplicate detection check`, {
      validationId,
      filename: file.name,
      contentLength: extractedText.length,
    });

    const duplicateCheck = await duplicateDetectionService.checkForDuplicates(
      extractedText,
      file.name
    );

    // Store duplicate info to block specific generation type after upload succeeds
    let duplicateTypeInfo: { isDuplicate: boolean; type?: string; message?: string } = {
      isDuplicate: false,
    };

    if (duplicateCheck.isDuplicate) {
      logWarn(`Duplicate content detected - will block specific generation type`, {
        validationId,
        reason: duplicateCheck.reason,
        similarity: duplicateCheck.similarityScore
          ? `${(duplicateCheck.similarityScore * 100).toFixed(0)}%`
          : "N/A",
        matchedType: duplicateCheck.matchedDocument?.type,
      });

      duplicateTypeInfo = {
        isDuplicate: true,
        type: duplicateCheck.matchedDocument?.type, // 'summary' or 'flashcards'
        message: duplicateCheck.message,
      };

      logDebug(`File will be allowed but generation type blocked:`, {
        validationId,
        blockedType: duplicateTypeInfo.type,
      });
    } else {
      logSuccess(`Duplicate detection check passed - content is unique`, {
        validationId,
      });
    }

    // CRITICAL: SUBJECT RELEVANCE CHECK - Block content that doesn't match session subject
    // Only perform this check if we have a session context with subject
    if (props.sessionContext?.subject) {
      logDebug(`Performing subject relevance check`, {
        validationId,
        expectedSubject: props.sessionContext.subject,
      });

      const relevanceCheck = await geminiService.validateSubjectRelevance(
        extractedText,
        props.sessionContext.subject
      );

      logDebug(`Subject relevance check completed`, {
        validationId,
        isRelevant: relevanceCheck.isRelevant,
        relevanceScore: relevanceCheck.relevanceScore,
        detectedSubject: relevanceCheck.detectedSubject,
        expectedSubject: relevanceCheck.expectedSubject,
        confidence: relevanceCheck.confidence,
      });

      // Block if relevance score is too low (below 60%)
      if (!relevanceCheck.isRelevant) {
        logWarn(`File blocked due to subject mismatch`, {
          validationId,
          relevanceScore: relevanceCheck.relevanceScore,
          detectedSubject: relevanceCheck.detectedSubject,
          expectedSubject: relevanceCheck.expectedSubject,
        });

        return {
          isValid: false,
          message: `REJECT: ${relevanceCheck.message}`,
        };
      }

      // Log successful relevance check
      logSuccess(`Subject relevance validated successfully`, {
        validationId,
        relevanceScore: relevanceCheck.relevanceScore,
        detectedSubject: relevanceCheck.detectedSubject,
      });
    } else {
      logDebug(`Skipping subject relevance check - no session context available`, {
        validationId,
      });
    }

    // Calculate readability percentage (improved calculation for this file)
    const words = extractedText.split(/\s+/).filter((word) => word.length > 0);
    const meaningfulWords = words.filter((word) => {
      // Remove common punctuation from word ends for better recognition
      const cleanWord = word.replace(/^[^\w]+|[^\w]+$/g, "");
      // Accept words that are mostly alphabetic (including contractions, hyphens, numbers)
      return (
        cleanWord.length >= 1 &&
        (/^[a-zA-Z][a-zA-Z'-]*[a-zA-Z]?$/.test(cleanWord) || /^\d+$/.test(cleanWord))
      );
    });
    const readabilityPercentage =
      words.length > 0 ? Math.round((meaningfulWords.length / words.length) * 100) : 0;

    logSuccess(
      `Validation successful: ${extractedText.length} chars, ${readabilityPercentage}% readable (${meaningfulWords.length}/${words.length} words)`,
      { validationId }
    );

    const successMessage = `VALID: ${extractedText.length.toLocaleString()} characters, ${readabilityPercentage}% readable text, suitable for AI processing.`;

    return {
      isValid: true,
      message: successMessage,
      text: extractedText,
      duplicateInfo: duplicateTypeInfo, // Pass duplicate info to caller
    };
  } catch (error: unknown) {
    logError(`Validation error:`, { validationId, error });
    const errorMessage =
      error instanceof Error
        ? error.message
        : "REJECT: An unexpected error occurred while processing your file. The file may be corrupted, in an unsupported format, or too complex to analyze. Please try uploading a different file or convert your content to a simpler format (plain text or basic PDF).";
    logDebug(`Validation failed with error: ${errorMessage}`, { validationId });
    return {
      isValid: false,
      message: errorMessage,
    };
  }
};

const triggerFileInput = () => {
  if (uploadedFiles.value.length > 0) {
    Swal.fire({
      icon: "info",
      title: "File Already Uploaded",
      html: `You already have a file uploaded.<br><br>Please <strong>remove the current file</strong> first if you want to upload a different one.`,
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "Got it",
    });
    return;
  }

  if (isFileUploadDisabled.value) {
    Swal.fire({
      icon: "info",
      title: "Cannot Upload File",
      html: `You have text pasted in the notes area below.<br><br>Please <strong>clear the notes text</strong> first if you want to upload a file instead.`,
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "Got it",
    });
    return;
  }

  fileInputRef.value?.click();
};

const handleFileSelect = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    await handleFiles(Array.from(target.files));
    // Clear the input value to ensure fresh state for next selection
    target.value = "";
  }
};

const handleFiles = async (files: File[]) => {
  const processingId = `processing_${Date.now()}`;
  logDebug(`Processing ${files.length} file(s)`, { processingId });

  // Check if user is trying to upload more than 1 file or already has a file
  if (uploadedFiles.value.length > 0) {
    logDebug(`File limit reached - user already has a file uploaded`, { processingId });
    Swal.fire({
      icon: "warning",
      title: "File Limit Reached",
      html: `You can only upload <strong>1 file</strong> at a time.<br><br>Please remove the current file first if you want to upload a different one.`,
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "Got it",
    });
    return;
  }

  // Check if text is already pasted
  if (isFileUploadDisabled.value) {
    logDebug(`File upload disabled - user has pasted text`, { processingId });
    Swal.fire({
      icon: "warning",
      title: "Cannot Upload File",
      html: `You have text pasted in the notes area below.<br><br>Please <strong>clear the notes text</strong> first if you want to upload a file instead.`,
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "Got it",
    });
    return;
  }

  if (files.length > 1) {
    logDebug(`Multiple files detected - only one allowed`, { processingId });
    Swal.fire({
      icon: "warning",
      title: "Multiple Files Not Allowed",
      html: `You can only upload <strong>1 file</strong> at a time.<br><br>Please select only one file and try again.`,
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "Got it",
    });
    return;
  }

  const file = files[0];
  logDebug(`Processing single file: ${file.name}, type: ${file.type}, size: ${file.size}`, {
    processingId,
  });

  // Check file type and size first
  if (!isValidFileType(file)) {
    logDebug(`Invalid file type for file: ${file.name}`, { processingId });
    Swal.fire({
      icon: "warning",
      title: "Invalid File Type",
      html: `File "<strong>${file.name}</strong>" is not a supported file type.<br><br>Please upload PDF, TXT, or DOCX files only.`,
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "Choose Another File",
    });
    return;
  }

  if (!isValidFileSize(file)) {
    logDebug(`File too large: ${file.name} (${file.size} bytes)`, { processingId });
    Swal.fire({
      icon: "warning",
      title: "File Too Large",
      html: `File "<strong>${file.name}</strong>" is too large.<br><br>Maximum file size is <strong>10MB</strong>.`,
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "Choose Smaller File",
    });
    return;
  }

  // ✅ SECURITY: Verify MIME type (first layer of defense)
  logDebug(`🔒 Step 1: MIME type validation for ${file.name}`, { processingId });
  const mimeCheck = verifyMIMEType(file);

  if (!mimeCheck.isValid) {
    logError(`❌ MIME type validation failed for ${file.name}`, {
      processingId,
      reason: mimeCheck.reason,
    });

    Swal.fire({
      icon: "error",
      title: "🚨 Security Alert",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p style="margin-bottom: 15px; font-weight: 600; color: #dc2626;">
            File upload blocked for security reasons:
          </p>
          <p style="margin-bottom: 15px; font-size: 14px; line-height: 1.6;">
            ${mimeCheck.reason}
          </p>
          <p style="font-size: 13px; color: #6b7280; line-height: 1.5;">
            <strong>What to do:</strong><br>
            • Make sure you're uploading a genuine PDF, TXT, or DOCX file<br>
            • Don't rename executable files (.exe, .app, etc.)<br>
            • If this is a legitimate file, contact support
          </p>
        </div>
      `,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Understood",
      customClass: {
        popup: "elevated-modal",
      },
    });
    return;
  }

  logSuccess(`✅ MIME type validation passed for ${file.name}`, { processingId });

  // ✅ SECURITY: Verify file signature (magic bytes) - most critical check
  logDebug(`🔒 Step 2: File signature validation for ${file.name}`, { processingId });
  const signatureCheck = await verifyFileSignature(file);

  if (!signatureCheck.isValid) {
    logError(`❌ CRITICAL: File signature validation failed for ${file.name}`, {
      processingId,
      detectedType: signatureCheck.detectedType,
      reason: signatureCheck.reason,
    });

    Swal.fire({
      icon: "error",
      title: "🛡️ Security Threat Blocked",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p style="margin-bottom: 15px; font-weight: 700; color: #dc2626; font-size: 16px;">
            ⚠️ MALICIOUS FILE DETECTED
          </p>
          <p style="margin-bottom: 15px; font-size: 14px; line-height: 1.6; background: #fee2e2; padding: 12px; border-radius: 8px; border: 2px solid #dc2626;">
            ${signatureCheck.reason}
          </p>
          <p style="font-size: 13px; color: #1f2937; line-height: 1.6; margin-bottom: 10px;">
            <strong>File Analysis:</strong>
          </p>
          <ul style="font-size: 13px; color: #4b5563; line-height: 1.8; margin-left: 20px;">
            <li><strong>Filename:</strong> ${file.name}</li>
            <li><strong>Claims to be:</strong> ${file.name.split(".").pop()?.toUpperCase()}</li>
            <li><strong>Actually is:</strong> ${signatureCheck.detectedType.toUpperCase()}</li>
            <li><strong>Threat Level:</strong> <span style="color: #dc2626; font-weight: 700;">HIGH</span></li>
          </ul>
          <p style="font-size: 12px; color: #6b7280; margin-top: 15px; padding-top: 15px; border-top: 1px solid #e5e7eb;">
            <strong>🛡️ Your security is our priority.</strong> This file has been blocked to protect your device from potential malware.
          </p>
        </div>
      `,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Close",
      customClass: {
        popup: "elevated-modal",
      },
    });
    return;
  }

  logSuccess(`✅ File signature validation passed for ${file.name}`, {
    processingId,
    detectedType: signatureCheck.detectedType,
  });

  // ✅ SECURITY: Check for corrupted files (Step 3)
  logDebug(`🔒 Step 3: Corruption detection for ${file.name}`, { processingId });
  const corruptionCheck = await detectCorruptedFile(file);

  if (corruptionCheck.isCorrupted) {
    logError(`❌ Corrupted file rejected: ${file.name}`, {
      processingId,
      reason: corruptionCheck.reason,
    });

    Swal.fire({
      icon: "error",
      title: "⚠️ Corrupted File Detected",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p style="margin-bottom: 15px; font-weight: 600; color: #dc2626;">
            The uploaded file appears to be corrupted or incomplete.
          </p>
          <div style="background: #fee2e2; padding: 12px; border-radius: 8px; border: 2px solid #fca5a5; margin-bottom: 15px;">
            <p style="font-size: 13px; color: #1f2937; margin: 0;">
              <strong>Issue Detected:</strong><br>
              ${corruptionCheck.reason}
            </p>
          </div>
          <div style="background: #dbeafe; padding: 12px; border-radius: 8px; border: 2px solid #93c5fd;">
            <p style="font-size: 12px; color: #1e40af; margin: 0 0 8px 0; font-weight: 600;">
              💡 What you can do:
            </p>
            <ul style="font-size: 12px; color: #1e3a8a; margin: 0; padding-left: 20px; line-height: 1.6;">
              <li>Try re-saving the file from the original application</li>
              <li>Check if the file opens correctly on your device</li>
              <li>Convert the file to a different format (e.g., save as new PDF)</li>
              <li>Use a file repair tool if available</li>
            </ul>
          </div>
        </div>
      `,
      confirmButtonColor: "#2563eb",
      confirmButtonText: "Try Another File",
      customClass: {
        popup: "elevated-modal",
      },
    });
    return;
  }

  logSuccess(`✅ File integrity check passed for ${file.name}`, { processingId });

  // Show enhanced loading message while validating content
  logDebug(`Starting content validation for: ${file.name}`, { processingId });
  Swal.fire({
    title: "Validating File",
    html: `
      <div style="text-align: left; padding: 16px 8px;">
        <!-- Animated Document Icon -->
        <div style="display: flex; justify-content: center; margin-bottom: 20px;">
          <div style="position: relative; width: 70px; height: 70px;">
            <!-- Outer pulsing ring -->
            <div style="position: absolute; inset: 0; border-radius: 16px; background: rgba(16, 185, 129, 0.3); animation: validate-ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;"></div>
            <!-- Middle rotating border -->
            <div style="position: absolute; inset: 6px; border-radius: 14px; border: 3px solid rgba(59, 130, 246, 0.4); animation: validate-spin-slow 3s linear infinite;"></div>
            <!-- Inner gradient square -->
            <div style="position: relative; width: 100%; height: 100%; background: linear-gradient(135deg, #10b981 0%, #3b82f6 100%); border-radius: 12px; display: flex; align-items: center; justify-content: center; box-shadow: 0 8px 20px rgba(16, 185, 129, 0.5);">
              <!-- Document SVG Icon -->
              <svg style="width: 36px; height: 36px; color: white; position: relative; z-index: 10;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <!-- Animated checkmark sparkle -->
              <div style="position: absolute; top: 8px; right: 8px; width: 18px; height: 18px; background: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; animation: validate-pulse-slow 2s ease-in-out infinite;">
                <svg style="width: 12px; height: 12px; color: #10b981;" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- File Info Badge -->
        <div style="display: flex; justify-content: center; margin-bottom: 18px;">
          <div style="display: inline-flex; flex-direction: column; align-items: center; background: linear-gradient(145deg, #f0fdf4 0%, #dbeafe 100%); border: 2px solid #10b981; border-radius: 12px; padding: 10px 20px; box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2); max-width: 90%;">
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #059669; font-weight: 600; letter-spacing: 0.3px;">Processing File</p>
            <p style="margin: 0; font-size: 13px; color: #1f2937; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%;">${file.name}</p>
          </div>
        </div>

        <!-- Progress Section with Step Details -->
        <div style="background: linear-gradient(145deg, #ffffff 0%, #f9fafb 100%); border: 2px solid #e5e7eb; border-radius: 12px; padding: 16px; margin-bottom: 16px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);">
          <!-- Progress Header -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <div style="text-align: left;">
              <p style="margin: 0; font-size: 12px; color: #6b7280; font-weight: 600;">Validation Pipeline</p>
              <p id="validate-step-number" style="margin: 0; font-size: 11px; color: #9ca3af; font-weight: 500;">Step 1 of 5</p>
            </div>
            <div style="text-align: right;">
              <p id="validate-progress-percentage" style="margin: 0; font-size: 16px; color: #10b981; font-weight: 700;">0%</p>
              <p style="margin: 0; font-size: 10px; color: #9ca3af; font-weight: 500;">Complete</p>
            </div>
          </div>

          <!-- Enhanced Progress Bar -->
          <div style="position: relative; background: #e5e7eb; border-radius: 10px; height: 14px; overflow: hidden; border: 2px solid #d1d5db; box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);">
            <div id="validation-progress" style="position: absolute; inset: 0; background: linear-gradient(90deg, #10b981 0%, #3b82f6 100%); height: 100%; width: 0%; transition: width 0.7s cubic-bezier(0.4, 0, 0.2, 1);">
              <!-- Animated shimmer -->
              <div style="position: absolute; inset: 0; background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%); animation: validate-shimmer 2s infinite;"></div>
              <!-- Pulse at end -->
              <div style="position: absolute; right: 0; top: 0; bottom: 0; width: 2px; background: white; animation: validate-pulse 1s ease-in-out infinite;"></div>
            </div>
          </div>

          <!-- Time Estimate Badge -->
          <div style="display: flex; justify-content: center; margin-top: 12px;">
            <div style="display: inline-flex; align-items: center; gap: 6px; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border: 2px solid #f59e0b; border-radius: 8px; padding: 6px 12px;">
              <div style="position: relative;">
                <svg style="width: 14px; height: 14px; color: #d97706;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span style="position: absolute; top: 0; right: 0; width: 5px; height: 5px; background: #f59e0b; border-radius: 50%; animation: validate-ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;"></span>
              </div>
              <div style="font-size: 11px;">
                <span style="font-weight: 600; color: #92400e;">Est. Time:</span>
                <span style="margin-left: 4px; font-weight: 700; color: #b45309;">5-10 seconds</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Current Step Details Panel -->
        <div style="background: linear-gradient(145deg, #f0fdf4 0%, #dbeafe 100%); border: 2px solid #10b981; border-radius: 12px; padding: 16px; box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);">
          <!-- Current Step Title -->
          <p id="validate-step-title" style="margin: 0 0 12px 0; font-size: 13px; color: #065f46; font-weight: 700; display: flex; align-items: center; gap: 8px;">
            <span id="validate-step-icon" style="display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: white; border-radius: 6px; border: 2px solid #10b981;">
              <svg style="width: 14px; height: 14px; color: #10b981;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </span>
            Text Extraction
          </p>

          <!-- Step Description -->
          <div id="validate-step-details" style="space-y: 8px;">
            <div style="display: flex; align-items: start; gap: 8px; margin-bottom: 6px; animation: validate-fade-in 0.5s ease-out;">
              <span style="display: inline-block; width: 6px; height: 6px; background: #10b981; border-radius: 50%; margin-top: 6px; flex-shrink: 0; animation: validate-pulse 2s ease-in-out infinite;"></span>
              <span style="font-size: 12px; color: #065f46; font-weight: 500; line-height: 1.5;">Reading and extracting text from your file</span>
            </div>
            <div style="display: flex; align-items: start; gap: 8px; margin-bottom: 6px; animation: validate-fade-in 0.5s ease-out 0.1s both;">
              <span style="display: inline-block; width: 6px; height: 6px; background: #10b981; border-radius: 50%; margin-top: 6px; flex-shrink: 0; animation: validate-pulse 2s ease-in-out infinite;"></span>
              <span style="font-size: 12px; color: #065f46; font-weight: 500; line-height: 1.5;">Preserving document structure and formatting</span>
            </div>
          </div>
        </div>
      </div>

      <style>
        @keyframes validate-spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes validate-pulse-slow {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.15); opacity: 0.85; }
        }
        @keyframes validate-ping {
          0% { transform: scale(1); opacity: 1; }
          75%, 100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes validate-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes validate-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes validate-fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Mobile responsive adjustments */
        @media (max-width: 640px) {
          #validate-step-title {
            font-size: 12px !important;
          }
          #validate-step-details span {
            font-size: 11px !important;
          }
          #validate-progress-percentage {
            font-size: 14px !important;
          }
        }
      </style>
    `,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    width: "580px",
    customClass: {
      popup: "elevated-modal",
      title: "elevated-modal-title",
    },
    didOpen: () => {
      // Get DOM elements for updating
      const progressBar = document.getElementById("validation-progress");
      const progressPercentage = document.getElementById("validate-progress-percentage");
      const stepNumber = document.getElementById("validate-step-number");
      const stepTitle = document.getElementById("validate-step-title");
      const stepIcon = document.getElementById("validate-step-icon");
      const stepDetails = document.getElementById("validate-step-details");

      // Define detailed validation steps
      const steps = [
        {
          title: "Text Extraction",
          icon: `<svg style="width: 14px; height: 14px; color: #10b981;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>`,
          progress: 18,
          details: [
            "Reading and extracting text from your file",
            "Preserving document structure and formatting",
          ],
        },
        {
          title: "Content Quality Check",
          icon: `<svg style="width: 14px; height: 14px; color: #10b981;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>`,
          progress: 38,
          details: [
            "Analyzing content structure and coherence",
            "Verifying sufficient educational material",
            "Checking for complete and meaningful text",
          ],
        },
        {
          title: "Readability Assessment",
          icon: `<svg style="width: 14px; height: 14px; color: #10b981;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>`,
          progress: 58,
          details: [
            "Evaluating text clarity and comprehension",
            "Checking language complexity and structure",
            "Ensuring content is suitable for study materials",
          ],
        },
        {
          title: "Content Safety Scan",
          icon: `<svg style="width: 14px; height: 14px; color: #10b981;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>`,
          progress: 78,
          details: [
            "Verifying content is appropriate for education",
            "Checking for academic integrity standards",
            "Ensuring compliance with platform guidelines",
          ],
        },
        {
          title: "Final Verification",
          icon: `<svg style="width: 14px; height: 14px; color: #10b981;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>`,
          progress: 100,
          details: [
            "Completing validation checks",
            "Preparing file for AI processing",
            "Ready to generate study materials",
          ],
        },
      ];

      let currentStepIndex = 0;

      const updateValidationProgress = () => {
        if (currentStepIndex < steps.length) {
          const step = steps[currentStepIndex];

          // Update progress bar and percentage
          if (progressBar) progressBar.style.width = step.progress + "%";
          if (progressPercentage) progressPercentage.textContent = step.progress + "%";

          // Update step number
          if (stepNumber)
            stepNumber.textContent = `Step ${currentStepIndex + 1} of ${steps.length}`;

          // Update step title with icon
          if (stepTitle) {
            stepTitle.innerHTML = `
              <span id="validate-step-icon" style="display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: white; border-radius: 6px; border: 2px solid #10b981;">
                ${step.icon}
              </span>
              ${step.title}
            `;
          }

          // Update step details with staggered animations
          if (stepDetails) {
            stepDetails.innerHTML = step.details
              .map(
                (detail, index) => `
              <div style="display: flex; align-items: start; gap: 8px; margin-bottom: 6px; animation: validate-fade-in 0.5s ease-out ${
                index * 0.1
              }s both;">
                <span style="display: inline-block; width: 6px; height: 6px; background: #10b981; border-radius: 50%; margin-top: 6px; flex-shrink: 0; animation: validate-pulse 2s ease-in-out infinite;"></span>
                <span style="font-size: 12px; color: #065f46; font-weight: 500; line-height: 1.5;">${detail}</span>
              </div>
            `
              )
              .join("");
          }

          currentStepIndex++;

          // Variable timing: faster for simpler checks, slower for complex analysis
          const baseDelay = currentStepIndex < 2 ? 1200 : currentStepIndex < 4 ? 1800 : 2200;
          const randomVariation = Math.random() * 600;
          setTimeout(updateValidationProgress, baseDelay + randomVariation);
        }
      };

      // Start progress animation
      setTimeout(updateValidationProgress, 500);
    },
  });

  try {
    // Ensure fresh validation for each file
    logDebug(`About to call validateFileContent for: ${file.name}`, { processingId });
    const validation = await validateFileContent(file);
    logDebug(`Validation completed for ${file.name}:`, { processingId, validation });

    // Always close loading dialog first
    Swal.close();

    if (!validation.isValid) {
      logDebug(`File rejected: ${file.name} - ${validation.message}`, { processingId });
      // Show rejection message with specific, descriptive errors
      let title = "File Content Invalid";
      let message = "There was an issue with your file content.";

      if (
        validation.message.includes("character limit") ||
        validation.message.includes("exceeds the maximum")
      ) {
        title = "File Too Long";
        message =
          "Your file contains more than 10,000 characters, which exceeds our processing limit. This limit ensures quality AI generation and reasonable response times. Please split your content into smaller sections (aim for 2,000-10,000 characters each) or summarize the key points before uploading.";
      } else if (
        validation.message.includes("Insufficient content") ||
        validation.message.includes("only")
      ) {
        title = "File Too Short";
        message =
          "Your file needs to contain at least 2,000 characters for quality AI processing. Currently, it has fewer characters than required. Please upload a more substantial document with meaningful educational content, or combine multiple short notes into one file.";
      } else if (
        validation.message.includes("gibberish") ||
        validation.message.includes("unreadable")
      ) {
        title = "Content Not Readable";
        message =
          "The file content could not be interpreted as readable text. Our system detected mostly random characters, excessive symbols, or unrecognizable patterns. This usually means: (1) The file encoding is wrong (try saving as UTF-8), (2) The file is corrupted, or (3) The document contains unsupported special characters. Please check your file and ensure it contains clear, readable educational content.";
      } else if (
        validation.message.includes("blank pages") ||
        validation.message.includes("images") ||
        validation.message.includes("95%") ||
        validation.message.includes("80%")
      ) {
        title = "PDF Contains Only Images or Blank Pages";
        message =
          "Most pages in your PDF are either blank or contain only images with no extractable text. Our system can only process text-based PDFs. If your PDF is a scanned document or contains screenshots, you'll need to: (1) Use OCR (Optical Character Recognition) to convert images to text first, (2) Retype the content, or (3) Upload the original text-based document instead.";
      } else if (validation.message.includes("insufficient readable text")) {
        title = "Not Enough Text in PDF";
        message =
          "The PDF was successfully opened, but very little text could be extracted from it. This typically means: (1) The PDF contains mostly images, diagrams, or charts without text, (2) The text is embedded as images (scanned document), or (3) The PDF has internal formatting issues. Try using an OCR tool if it's a scanned document, or re-save the PDF from the original source.";
      } else if (
        validation.message.includes("Failed to extract text from PDF") ||
        validation.message.includes("password-protected")
      ) {
        title = "PDF Cannot Be Read";
        message =
          "We could not extract text from this PDF. Common causes: (1) The PDF is password-protected (remove the password first), (2) The file is corrupted or incomplete, (3) The PDF uses a non-standard format we don't support, or (4) The PDF contains only images without text. If password-protected, unlock it first. If scanned, use OCR. Otherwise, try converting to a plain text or Word document.";
      } else if (
        validation.message.includes("PDF processing service") ||
        validation.message.includes("worker") ||
        validation.message.includes("fetch") ||
        validation.message.includes("initialization")
      ) {
        title = "PDF Processing System Error";
        message =
          "Our PDF processing system encountered a technical issue and could not load properly. This is usually a temporary problem. Please try: (1) Refreshing your browser page and uploading again, (2) Using a different browser (Chrome recommended), or (3) Converting your PDF to a Word document or plain text file. If the problem persists after refreshing, the system may be temporarily unavailable.";
      } else if (
        validation.message.includes("Failed to extract text from TXT") ||
        validation.message.includes("text file could not be read")
      ) {
        title = "Text File Reading Error";
        message =
          "The text file could not be read properly. This usually happens because: (1) The file uses an incompatible character encoding (not UTF-8), (2) The file is corrupted or incomplete, or (3) The file was saved in a non-standard text format. Try opening the file in Notepad or another text editor, then 'Save As' and select UTF-8 encoding before uploading again.";
      } else if (
        validation.message.includes("Failed to extract text from DOCX") ||
        validation.message.includes("Word document could not be processed")
      ) {
        title = "Word Document Processing Error";
        message =
          "The Word document could not be processed. Common reasons: (1) The file is corrupted or was interrupted during download, (2) It's an older .doc format (not .docx) that needs conversion, (3) The document has internal formatting errors or is password-protected. Try: Opening the file in Microsoft Word → 'Save As' → save as a new .docx file. Or convert it to PDF or plain text format.";
      } else if (
        validation.message.includes("invalid Microsoft Word") ||
        validation.message.includes("invalid Microsoft Word structure")
      ) {
        title = "Invalid Word Document Format";
        message =
          "This file claims to be a Word document but has an invalid internal structure. This could mean: (1) The file extension was manually renamed from another format (not actually a Word doc), (2) The file is an older .doc format that needs to be converted to .docx, or (3) The document was corrupted. Try opening it in Microsoft Word and saving it as a new .docx file, or contact the person who sent you this file to get a valid copy.";
      } else if (
        validation.message.includes("Inappropriate content") ||
        validation.message.includes("content filter")
      ) {
        title = "Content Filter Detected Issues";
        message = validation.message.replace("REJECT: ", "");
      } else {
        // Use the specific error message from validation
        message = validation.message.replace("REJECT: ", "");
      }

      await Swal.fire({
        icon: "error",
        title: title,
        text: message,
        confirmButtonColor: "#3b82f6",
        confirmButtonText: "Try Another File",
      });
      logDebug(`Error message shown to user for: ${file.name}`, { processingId });
      return;
    }

    // File is valid - show success message and add to uploaded files
    logDebug(`File accepted: ${file.name}`, { processingId });
    await Swal.fire({
      icon: "success",
      title: "File Validated Successfully!",
      text: validation.message.replace("VALID: ", ""),
      confirmButtonColor: "#10b981",
      confirmButtonText: "Great!",
      timer: 3000,
      timerProgressBar: true,
    });

    uploadedFiles.value.push(file);
    logDebug(`File ${file.name} added to uploadedFiles array`, { processingId });

    // NEW: Check filename availability for type-specific button disabling
    await checkFilenameAvailability(file.name);

    // NEW: Handle duplicate content detection - block specific generation type
    if (validation.duplicateInfo?.isDuplicate && validation.duplicateInfo?.type) {
      const duplicateType = validation.duplicateInfo.type;
      logDebug(`Blocking generation type due to duplicate content:`, {
        processingId,
        blockedType: duplicateType,
        message: validation.duplicateInfo.message,
      });

      // ✅ FIX: Map singular types from duplicate service to plural UI types
      // Duplicate service returns: "summary" or "flashcard" (singular)
      // UI content types use: "summary" or "flashcards" (plural)
      if (duplicateType === "summary") {
        blockedGenerationTypes.value.summary = true;
        blockedGenerationTypes.value.summaryReason = validation.duplicateInfo.message;
      } else if (duplicateType === "flashcard") {
        // ✅ Changed from "flashcards" to "flashcard" to match service return type
        blockedGenerationTypes.value.flashcards = true;
        blockedGenerationTypes.value.flashcardsReason = validation.duplicateInfo.message;
      }

      // Show the floating duplicate notice
      showDuplicateNotice.value = true;

      // ✅ Display-friendly type name for user message
      const displayType = duplicateType === "flashcard" ? "flashcards" : duplicateType;

      // Show info message about the blocked type
      await Swal.fire({
        icon: "info",
        title: "Duplicate Content Detected",
        html: `${validation.duplicateInfo.message}<br><br>The file has been uploaded, but you cannot generate <strong>${displayType}</strong> again for this content.<br><br>You can still generate the other content type.`,
        confirmButtonColor: "#3b82f6",
        confirmButtonText: "I Understand",
      });
    }

    // Show AI Disclaimer popup after successful file upload
    await showAIDisclaimerPopup();
  } catch (error: unknown) {
    logError(`Unexpected error during file processing:`, { processingId, error });
    Swal.close(); // Ensure loading dialog is closed

    const errorMsg = error instanceof Error ? error.message : "Unknown processing error";
    const userMessage = errorMsg.includes("REJECT:")
      ? errorMsg.replace("REJECT: ", "")
      : "An unexpected error occurred while processing your file. This could be due to: (1) A temporary system issue, (2) File corruption, or (3) An incompatible file format. Please try uploading again, or use a different file format (PDF, DOCX, or TXT). If the error persists, try refreshing your browser.";

    await Swal.fire({
      icon: "error",
      title: "File Processing Failed",
      text: userMessage,
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "Try Again",
    });
  }
};

const removeFile = (index: number) => {
  uploadedFiles.value.splice(index, 1);

  // NEW: Reset blocked generation types when file is removed
  blockedGenerationTypes.value = {
    summary: false,
    flashcards: false,
    summaryReason: undefined,
    flashcardsReason: undefined,
  };

  // Reset duplicate notice visibility
  showDuplicateNotice.value = true;

  // Clear selected type if it was blocked
  if (selectedType.value) {
    selectedType.value = "";
  }
};

// Dismiss duplicate content notice (keeps the blocking, just hides the notice)
const dismissDuplicateNotice = () => {
  showDuplicateNotice.value = false;
  logDebug("User dismissed duplicate content notice");
};

// Show AI Disclaimer popup
const showAIDisclaimerPopup = async () => {
  await Swal.fire({
    title: "AI Content Notice",
    html: `
      <div style="text-align: left; padding: 20px; background: linear-gradient(135deg, #fef9e7 0%, #fef3c7 70%, #fde68a 100%); border-radius: 12px; margin: 15px 0; box-shadow: 0 4px 20px rgba(245, 158, 11, 0.2);">
        <p style="margin: 0 0 15px 0; font-size: 15px; color: #92400e; font-weight: 600;">
          <strong>Important:</strong> The AI-generated content in this app is for reference only and may contain errors.
        </p>
        <ul style="margin: 0 0 15px 0; padding-left: 20px; font-size: 14px; color: #92400e; line-height: 1.6; list-style: none;">
          <li style="margin-bottom: 8px;">🔍 We encourage you to double-check the information</li>
          <li style="margin-bottom: 8px;">👨‍🏫 Consult with your teachers or trusted sources</li>
          <li style="margin-bottom: 8px;">🤖 This app uses Google Gemini 2.5 Pro model (October 2025)</li>
        </ul>
        <div style="background: linear-gradient(135deg, #fef7ed 0%, #fed7aa 100%); padding: 16px; border-radius: 8px; margin-top: 15px; border: 1px solid #fdba74;">
          <p style="margin: 0; font-size: 15px; color: #92400e; text-align: center; font-weight: 600;">
            If you find anything unusual, please email us at
            <a href="mailto:elevated.tutorapp@gmail.com" style="color: #b45309; text-decoration: none; font-weight: 700; text-shadow: 0 1px 2px rgba(180, 83, 9, 0.3); font-size: 16px;">elevated.tutorapp@gmail.com</a>
          </p>
        </div>
      </div>
    `,
    icon: "warning",
    confirmButtonText: "I Understand",
    confirmButtonColor: "#f59e0b",
    allowOutsideClick: false,
    allowEscapeKey: false,
    width: "600px",
    customClass: {
      popup: "ai-disclaimer-popup",
      title: "ai-disclaimer-title",
      htmlContainer: "ai-disclaimer-content",
      confirmButton: "ai-disclaimer-button",
    },
  });
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  // Don't allow drag over if file already uploaded or if text input is present
  if (uploadedFiles.value.length > 0 || isFileUploadDisabled.value) {
    return;
  }
};

const handleDragEnter = (event: DragEvent) => {
  event.preventDefault();
  // Don't allow drag enter if file already uploaded or if text input is present
  if (uploadedFiles.value.length > 0 || isFileUploadDisabled.value) {
    return;
  }
  isDragOver.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault();
  if (
    !event.relatedTarget ||
    !(event.currentTarget as Element).contains(event.relatedTarget as Node)
  ) {
    isDragOver.value = false;
  }
};

const handleFileDrop = async (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = false;

  // Don't allow drop if file already uploaded
  if (uploadedFiles.value.length > 0) {
    Swal.fire({
      icon: "warning",
      title: "File Already Uploaded",
      html: `You already have a file uploaded.<br><br>Please <strong>remove the current file</strong> first if you want to upload a different one.`,
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "Got it",
    });
    return;
  }

  // Don't allow drop if text is already pasted
  if (isFileUploadDisabled.value) {
    Swal.fire({
      icon: "warning",
      title: "Cannot Upload File",
      html: `You have text pasted in the notes area below.<br><br>Please <strong>clear the notes text</strong> first if you want to upload a file instead.`,
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "Got it",
    });
    return;
  }

  if (event.dataTransfer?.files) {
    await handleFiles(Array.from(event.dataTransfer.files));
  }
};

const generateContent = async () => {
  // NEW: Progressive disclosure - Validate that button should be enabled
  if (!isGenerateButtonEnabled.value) {
    if (!hasValidContent.value) {
      Swal.fire({
        icon: "info",
        title: "No Content Provided",
        html: "Please upload a file or paste your notes before generating AI content.",
        confirmButtonColor: "#3b82f6",
        confirmButtonText: "OK",
      });
      return;
    }

    if (!selectedType.value) {
      Swal.fire({
        icon: "info",
        title: "No Content Type Selected",
        html: "Please select a content type (Summary or Flashcards) before generating.",
        confirmButtonColor: "#3b82f6",
        confirmButtonText: "OK",
      });
      return;
    }
  }

  // Validate that user has selected a content type
  if (!selectedType.value) {
    Swal.fire({
      icon: "warning",
      title: "No Content Type Selected",
      text: "Please select a content type (Summary or Flashcards) before generating.",
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "OK",
    });
    return;
  }

  // Check if user has provided content (either file or text)
  const hasContent = uploadedFiles.value.length > 0 || notesText.value.trim().length > 0;
  if (!hasContent) {
    Swal.fire({
      icon: "warning",
      title: "No Content Provided",
      text: "Please upload a file or paste your notes before generating AI content.",
      confirmButtonColor: "#3b82f6",
      confirmButtonText: "OK",
    });
    return;
  }

  // ✅ NEW: Validate minimum character length for pasted text
  if (uploadedFiles.value.length === 0 && notesText.value.trim().length < 2000) {
    Swal.fire({
      icon: "warning",
      title: "Content Too Short",
      html: `Your pasted notes contain only <strong>${notesText.value
        .trim()
        .length.toLocaleString()}</strong> characters.<br><br>Minimum required: <strong>2,000 characters</strong><br>You need <strong>${(
        2000 - notesText.value.trim().length
      ).toLocaleString()}</strong> more characters for quality AI processing.`,
      confirmButtonColor: "#f59e0b",
      confirmButtonText: "Add More Content",
    });
    return;
  }

  // CRITICAL: DUPLICATE DETECTION CHECK FOR PASTED TEXT - Block duplicate content
  // NEW: Use type-specific duplicate detection (only checks content similarity for pasted text)
  if (uploadedFiles.value.length === 0 && notesText.value.trim().length > 0) {
    logInfo("Checking pasted text for duplicates before generation", {
      targetType: selectedType.value,
    });

    const targetType = selectedType.value === "summary" ? "summary" : "flashcard";

    const duplicateCheck = await duplicateDetectionService.checkForDuplicates(
      notesText.value.trim(),
      undefined, // No filename for pasted text
      targetType as "summary" | "flashcard" // Target type is 3rd parameter, not 4th
    );

    if (duplicateCheck.isDuplicate) {
      logWarn("Pasted text blocked due to duplicate detection", {
        reason: duplicateCheck.reason,
        targetType,
        similarity: duplicateCheck.similarityScore
          ? `${(duplicateCheck.similarityScore * 100).toFixed(0)}%`
          : "N/A",
      });

      Swal.fire({
        icon: "warning",
        title: "Duplicate Content Detected",
        html: duplicateCheck.message,
        confirmButtonColor: "#f59e0b",
        confirmButtonText: "Use Different Content",
      });
      return;
    }

    logSuccess("Duplicate check passed for pasted text");
  }

  // ✅ PERFORMANCE FIX: Prepare loading dialog content (will show AFTER validation)
  const loadingTitle =
    selectedType.value === "summary" ? "Generating Your Summary" : "Creating Your Flashcards";

  const aiModel = selectedType.value === "summary" ? "Gemini 2.5-Pro" : "Gemini 2.5-Flash";
  const estimatedTime = selectedType.value === "summary" ? "40-60" : "30-45";

  const loadingText = `
    <div style="text-align: left; padding: 16px 8px;">
      <!-- Animated AI Brain Icon -->
      <div style="display: flex; justify-content: center; margin-bottom: 20px;">
        <div style="position: relative; width: 80px; height: 80px;">
          <!-- Outer pulsing ring -->
          <div style="position: absolute; inset: 0; border-radius: 50%; background: rgba(139, 92, 246, 0.3); animation: ai-ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;"></div>
          <!-- Middle rotating ring -->
          <div style="position: absolute; inset: 8px; border-radius: 50%; border: 3px solid rgba(139, 92, 246, 0.4); animation: ai-spin-slow 3s linear infinite;"></div>
          <!-- Inner gradient circle -->
          <div style="position: relative; width: 100%; height: 100%; background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 25px rgba(139, 92, 246, 0.5);">
            <!-- Sparkling effect -->
            <div style="position: absolute; inset: 0; border-radius: 50%; overflow: hidden;">
              <div style="position: absolute; top: 0; right: 0; width: 32px; height: 32px; background: rgba(255, 255, 255, 0.3); border-radius: 50%; transform: translate(50%, -50%); animation: ai-pulse-slow 2s ease-in-out infinite;"></div>
              <div style="position: absolute; bottom: 0; left: 0; width: 24px; height: 24px; background: rgba(255, 255, 255, 0.2); border-radius: 50%; transform: translate(-50%, 50%); animation: ai-pulse-slow 2s ease-in-out infinite 0.5s;"></div>
            </div>
            <!-- AI Icon SVG -->
            <svg style="width: 40px; height: 40px; position: relative; z-index: 10; color: white;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
        </div>
      </div>

      <!-- AI Model Badge -->
      <div style="display: flex; justify-content: center; margin-bottom: 16px;">
        <div style="display: inline-flex; align-items: center; gap: 8px; background: linear-gradient(135deg, #f3e8ff 0%, #dbeafe 100%); border: 2px solid #8b5cf6; border-radius: 20px; padding: 8px 16px; box-shadow: 0 2px 8px rgba(139, 92, 246, 0.2);">
          <div style="width: 24px; height: 24px; background: linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%); border-radius: 6px; display: flex; align-items: center; justify-content: center;">
            <svg style="width: 14px; height: 14px; color: white;" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 7H7v6h6V7z" />
              <path fill-rule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clip-rule="evenodd" />
            </svg>
          </div>
          <div style="text-align: left;">
            <p style="margin: 0; font-size: 11px; color: #7c3aed; font-weight: 600; letter-spacing: 0.5px;">Powered by</p>
            <p style="margin: 0; font-size: 13px; color: #1f2937; font-weight: 700;">${aiModel}</p>
          </div>
        </div>
      </div>

      <!-- Progress Bar with Step Indicator -->
      <div style="background: linear-gradient(145deg, #ffffff 0%, #f9fafb 100%); border: 2px solid #e5e7eb; border-radius: 12px; padding: 16px; margin-bottom: 16px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);">
        <!-- Progress Stats -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
          <div style="text-align: left;">
            <p style="margin: 0; font-size: 12px; color: #6b7280; font-weight: 600;">AI Generation Pipeline</p>
            <p id="current-step-number" style="margin: 0; font-size: 11px; color: #9ca3af; font-weight: 500;">Step 1 of 6</p>
          </div>
          <div style="text-align: right;">
            <p id="progress-percentage" style="margin: 0; font-size: 16px; color: #8b5cf6; font-weight: 700;">0%</p>
            <p style="margin: 0; font-size: 10px; color: #9ca3af; font-weight: 500;">Complete</p>
          </div>
        </div>
        
        <!-- Enhanced Progress Bar -->
        <div style="position: relative; background: #e5e7eb; border-radius: 10px; height: 14px; overflow: hidden; border: 2px solid #d1d5db; box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);">
          <div id="progress-bar" style="position: absolute; inset: 0; background: linear-gradient(90deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%); height: 100%; width: 0%; transition: width 0.7s cubic-bezier(0.4, 0, 0.2, 1);">
            <!-- Animated shimmer -->
            <div style="position: absolute; inset: 0; background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%); animation: ai-shimmer 2s infinite;"></div>
            <!-- Pulse at end -->
            <div style="position: absolute; right: 0; top: 0; bottom: 0; width: 2px; background: white; animation: ai-pulse 1s ease-in-out infinite;"></div>
          </div>
        </div>

        <!-- Time Estimate Badge -->
        <div style="display: flex; justify-content: center; margin-top: 12px;">
          <div style="display: inline-flex; align-items: center; gap: 6px; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border: 2px solid #f59e0b; border-radius: 8px; padding: 6px 12px;">
            <div style="position: relative;">
              <svg style="width: 16px; height: 16px; color: #d97706;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span style="position: absolute; top: 0; right: 0; width: 6px; height: 6px; background: #f59e0b; border-radius: 50%; animation: ai-ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;"></span>
            </div>
            <div style="font-size: 11px;">
              <span style="font-weight: 600; color: #92400e;">Estimated Time:</span>
              <span style="margin-left: 4px; font-weight: 700; color: #b45309;">${estimatedTime} seconds</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Current Step Details Panel -->
      <div style="background: linear-gradient(145deg, #f0f9ff 0%, #e0e7ff 100%); border: 2px solid #8b5cf6; border-radius: 12px; padding: 16px; margin-bottom: 16px; box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);">
        <!-- Current Step Title -->
        <p id="current-step-title" style="margin: 0 0 12px 0; font-size: 13px; color: #5b21b6; font-weight: 700; display: flex; align-items: center; gap: 8px;">
          <span id="step-icon" style="display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: white; border-radius: 6px; border: 2px solid #8b5cf6;">
            <svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </span>
          Initializing AI Pipeline
        </p>
        
        <!-- Step Description -->
        <div id="step-details" style="space-y: 8px;">
          <div style="display: flex; align-items: start; gap: 8px; margin-bottom: 6px; animation: ai-fade-in 0.5s ease-out;">
            <span style="display: inline-block; width: 6px; height: 6px; background: #8b5cf6; border-radius: 50%; margin-top: 6px; flex-shrink: 0; animation: ai-pulse 2s ease-in-out infinite;"></span>
            <span style="font-size: 12px; color: #4c1d95; font-weight: 500; line-height: 1.5;">Preparing AI models and loading your content</span>
          </div>
        </div>
      </div>

      <!-- Important Notice -->
      <div style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border: 2px solid #f59e0b; border-radius: 10px; padding: 12px; box-shadow: 0 2px 6px rgba(245, 158, 11, 0.2);">
        <div style="display: flex; align-items: start; gap: 10px;">
          <svg style="width: 20px; height: 20px; color: #d97706; flex-shrink: 0; margin-top: 2px;" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
          <div style="flex: 1;">
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #92400e; font-weight: 700;">Please Keep This Window Open</p>
            <p style="margin: 0; font-size: 11px; color: #78350f; line-height: 1.5; font-weight: 500;">
              Do not refresh, close this tab, or navigate away while your content is being generated.
            </p>
          </div>
        </div>
      </div>
    </div>

    <style>
      @keyframes ai-spin-slow {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes ai-pulse-slow {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.1); opacity: 0.8; }
      }
      @keyframes ai-ping {
        0% { transform: scale(1); opacity: 1; }
        75%, 100% { transform: scale(2); opacity: 0; }
      }
      @keyframes ai-shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      @keyframes ai-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      @keyframes ai-fade-in {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      /* Mobile responsive adjustments */
      @media (max-width: 640px) {
        #current-step-title {
          font-size: 12px !important;
        }
        #step-details span {
          font-size: 11px !important;
        }
        #progress-percentage {
          font-size: 14px !important;
        }
      }
    </style>
  `;

  // ✅ SHOW LOADING MODAL IMMEDIATELY to provide instant feedback
  // Set loading state first to disable button
  aiContentStore.setLoading(true);

  // Show modal immediately before any async operations
  Swal.fire({
    title: loadingTitle,
    html: loadingText,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    width: "580px",
    customClass: {
      popup: "elevated-modal",
      title: "elevated-modal-title",
    },
    didOpen: async () => {
      // Get DOM elements for updating
      const progressBar = document.getElementById("progress-bar");
      const progressPercentage = document.getElementById("progress-percentage");
      const stepNumber = document.getElementById("current-step-number");
      const stepTitle = document.getElementById("current-step-title");
      const stepIcon = document.getElementById("step-icon");
      const stepDetails = document.getElementById("step-details");

      // Define detailed steps for each type
      const steps =
        selectedType.value === "summary"
          ? [
              {
                title: "Content Validation",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>`,
                progress: 12,
                details: [
                  "Checking content structure and quality",
                  "Verifying text length and readability",
                  "Ensuring content is suitable for AI processing",
                ],
              },
              {
                title: "Duplicate Detection",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>`,
                progress: 24,
                details: [
                  "Scanning for previously generated summaries",
                  "Comparing content against existing database",
                  "Ensuring fresh AI generation without duplicates",
                ],
              },
              {
                title: "Content Extraction",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>`,
                progress: 38,
                details: [
                  "Extracting text from your uploaded file",
                  "Preserving formatting and structure",
                  "Preparing content for AI analysis",
                ],
              },
              {
                title: "Concept Identification",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>`,
                progress: 55,
                details: [
                  "AI analyzing main topics and themes",
                  "Identifying key concepts and relationships",
                  "Extracting most important information",
                ],
              },
              {
                title: "Summary Generation",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>`,
                progress: 78,
                details: [
                  "Gemini 2.5-Pro AI crafting your summary",
                  "Synthesizing information into clear sections",
                  "Ensuring comprehensive yet concise output",
                ],
              },
              {
                title: "Quality Assurance",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>`,
                progress: 100,
                details: [
                  "Validating summary accuracy and completeness",
                  "Formatting content for readability",
                  "Final quality checks before delivery",
                ],
              },
            ]
          : [
              {
                title: "Content Validation",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>`,
                progress: 10,
                details: [
                  "Checking content structure and quality",
                  "Verifying sufficient material for flashcards",
                  "Ensuring content meets AI requirements",
                ],
              },
              {
                title: "Duplicate Detection",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>`,
                progress: 20,
                details: [
                  "Scanning for previously generated flashcards",
                  "Comparing content against existing database",
                  "Ensuring unique flashcard generation",
                ],
              },
              {
                title: "Content Extraction",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>`,
                progress: 32,
                details: [
                  "Extracting text from your uploaded file",
                  "Identifying key facts and concepts",
                  "Preparing content for AI processing",
                ],
              },
              {
                title: "Learning Point Identification",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>`,
                progress: 50,
                details: [
                  "AI analyzing content for testable concepts",
                  "Identifying 20 most important learning points",
                  "Determining optimal question difficulty",
                ],
              },
              {
                title: "Question Generation",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>`,
                progress: 70,
                details: [
                  "Gemini 2.5-Flash AI crafting questions",
                  "Creating diverse question types",
                  "Ensuring questions test understanding",
                ],
              },
              {
                title: "Answer Creation",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>`,
                progress: 88,
                details: [
                  "Generating accurate and detailed answers",
                  "Adding helpful explanations",
                  "Verifying answer correctness",
                ],
              },
              {
                title: "Quality Assurance",
                icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>`,
                progress: 100,
                details: [
                  "Validating all 20 flashcards for quality",
                  "Ensuring variety and proper coverage",
                  "Final formatting and preparation",
                ],
              },
            ];

      let currentStepIndex = 0;

      const updateProgress = () => {
        if (currentStepIndex < steps.length) {
          const step = steps[currentStepIndex];

          // Update progress bar and percentage
          if (progressBar) progressBar.style.width = step.progress + "%";
          if (progressPercentage) progressPercentage.textContent = step.progress + "%";

          // Update step number
          if (stepNumber)
            stepNumber.textContent = `Step ${currentStepIndex + 1} of ${steps.length}`;

          // Update step title with icon
          if (stepTitle) {
            stepTitle.innerHTML = `
              <span id="step-icon" style="display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: white; border-radius: 6px; border: 2px solid #8b5cf6;">
                ${step.icon}
              </span>
              ${step.title}
            `;
          }

          // Update step details with animations
          if (stepDetails) {
            stepDetails.innerHTML = step.details
              .map(
                (detail, index) => `
              <div style="display: flex; align-items: start; gap: 8px; margin-bottom: 6px; animation: ai-fade-in 0.5s ease-out ${
                index * 0.1
              }s both;">
                <span style="display: inline-block; width: 6px; height: 6px; background: #8b5cf6; border-radius: 50%; margin-top: 6px; flex-shrink: 0; animation: ai-pulse 2s ease-in-out infinite;"></span>
                <span style="font-size: 12px; color: #4c1d95; font-weight: 500; line-height: 1.5;">${detail}</span>
              </div>
            `
              )
              .join("");
          }

          currentStepIndex++;

          // Variable timing: earlier steps faster, later steps (AI generation) slower
          const baseDelay = currentStepIndex < 3 ? 3000 : currentStepIndex < 5 ? 5000 : 7000;
          const randomVariation = Math.random() * 2000;
          setTimeout(updateProgress, baseDelay + randomVariation);
        }
      };

      // Start progress animation after a brief delay
      setTimeout(updateProgress, 800);

      // ✅ NOW perform heavy operations AFTER modal is shown
      try {
        let contentToAnalyze = "";
        let fileName = "";

        if (uploadedFiles.value.length > 0) {
          const file = uploadedFiles.value[0];
          fileName = file.name;

          // Extract text from file based on type
          const fileExtension = file.name.toLowerCase().split(".").pop();
          switch (fileExtension) {
            case "pdf":
              contentToAnalyze = await extractTextFromPDF(file);
              break;
            case "txt":
              contentToAnalyze = await extractTextFromTXT(file);
              break;
            case "docx":
              contentToAnalyze = await extractTextFromDOCX(file);
              break;
            default:
              throw new Error("Unsupported file type");
          }
        } else {
          contentToAnalyze = notesText.value.trim();
          fileName = ""; // No file name for pasted text
        }

        // ✅ OPTIMIZATION: Skip redundant validation for uploaded files (already validated during upload)
        // Only validate pasted text for academic legitimacy
        const isUploadedFile = uploadedFiles.value.length > 0;
        let academicValidation: any = null;

        if (isUploadedFile) {
          // Skip validation - file was already validated in validateFileContent() during upload
          logInfo(
            "⚡ Skipping academic validation for uploaded file (already validated during upload)",
            {
              fileName,
              contentLength: contentToAnalyze.length,
            }
          );
          // Create a mock valid result to skip validation
          academicValidation = {
            isValid: true,
            isAcademic: true,
            detectedContentType: "Pre-validated file upload",
            matchedSubjects: [],
            confidenceScore: 100,
            reasoning: "File was validated during upload process",
            warnings: [],
            errors: [],
          };
        } else {
          // ✅ ONLY FOR PASTED TEXT: Validate academic content legitimacy
          logInfo("🎓 Starting academic content validation for pasted text", {
            contentLength: contentToAnalyze.length,
            sessionSubject: props.sessionContext?.subject,
          });

          // ✅ FIX: Update existing modal instead of creating new one
          const statusMessage = document.getElementById("current-step");
          const progressBar = document.getElementById("progress-bar");

          if (statusMessage) statusMessage.textContent = "🔍 Validating content legitimacy...";
          if (progressBar) progressBar.style.width = "10%";

          // Perform academic content validation (without creating new modal)
          academicValidation = await validateAcademicContent(
            contentToAnalyze,
            props.sessionContext?.subject
          );

          logDebug("Academic validation completed", {
            isValid: academicValidation.isValid,
            isAcademic: academicValidation.isAcademic,
            detectedContentType: academicValidation.detectedContentType,
            matchedSubjects: academicValidation.matchedSubjects,
            confidenceScore: academicValidation.confidenceScore,
          });

          // ✅ Continue with main progress (no modal close/reopen)
          if (statusMessage) statusMessage.textContent = "✅ Content validated - proceeding...";
          if (progressBar) progressBar.style.width = "20%";
        }

        // Block if content is not legitimate academic material
        if (!academicValidation.isValid) {
          logWarn("❌ Content blocked - not legitimate academic material", {
            detectedContentType: academicValidation.detectedContentType,
            confidenceScore: academicValidation.confidenceScore,
            errors: academicValidation.errors,
          });

          aiContentStore.setLoading(false);

          await Swal.fire({
            icon: "error",
            title: "⚠️ Invalid Academic Content",
            html: `
          <div class="text-left" style="max-height: 400px; overflow-y: auto;">
            <div class="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-3">
              <p class="mb-2"><strong>Detected Content Type:</strong> <span class="text-red-700">${
                academicValidation.detectedContentType
              }</span></p>
              <p class="mb-2"><strong>Confidence Score:</strong> <span class="text-red-700">${
                academicValidation.confidenceScore
              }%</span></p>
              ${
                academicValidation.matchedSubjects.length > 0
                  ? `<p class="mb-2"><strong>Detected Subjects:</strong> <span class="text-gray-700">${academicValidation.matchedSubjects.join(
                      ", "
                    )}</span></p>`
                  : ""
              }
            </div>
            <p class="mb-3 text-sm text-gray-700">${academicValidation.reasoning}</p>
            ${
              academicValidation.errors.length > 0
                ? `
              <div class="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-3 mb-3">
                <p class="font-semibold text-sm mb-2">Specific Issues Found:</p>
                <ul class="list-disc list-inside text-sm text-gray-700">
                  ${academicValidation.errors.map((err: string) => `<li>${err}</li>`).join("")}
                </ul>
              </div>
            `
                : ""
            }
            <div class="bg-green-50 border-2 border-green-300 rounded-lg p-3">
              <p class="font-semibold text-sm mb-2">✅ Allowed Content:</p>
              <p class="text-xs text-gray-600 mb-2">Please upload legitimate study notes or materials from these subjects:</p>
              <ul class="text-xs text-gray-700 space-y-1">
                ${ALLOWED_SUBJECTS.map((subject) => `<li>• ${subject}</li>`).join("")}
              </ul>
            </div>
          </div>
        `,
            confirmButtonText: "Update Content",
            confirmButtonColor: "#ef4444",
            width: "600px",
            customClass: {
              popup: "elevated-modal",
              title: "elevated-modal-title",
            },
          });
          return; // Exit early - don't proceed
        }

        // Log successful academic validation
        logSuccess("✅ Academic content validated successfully", {
          isAcademic: academicValidation.isAcademic,
          detectedContentType: academicValidation.detectedContentType,
          matchedSubjects: academicValidation.matchedSubjects,
          confidenceScore: academicValidation.confidenceScore,
        });

        // ✅ SECOND: Subject relevance check (only for pasted text)
        // ✅ CRITICAL: Subject validation logging
        logDebug("🔍 AINoteProcessor - checking subject relevance conditions", {
          hasSessionContext: !!props.sessionContext,
          hasSessionSubject: !!props.sessionContext?.subject,
          sessionSubject: props.sessionContext?.subject,
          hasUploadedFiles: uploadedFiles.value.length > 0,
          usingPastedText: !uploadedFiles.value.length,
          contentLength: contentToAnalyze.length,
        });

        // CRITICAL: SUBJECT RELEVANCE CHECK - Block pasted text that doesn't match session subject
        // Only perform this check if we have a session context with subject AND using pasted text
        if (props.sessionContext?.subject && !uploadedFiles.value.length) {
          logInfo("Performing subject relevance check on pasted text", {
            expectedSubject: props.sessionContext.subject,
            contentLength: contentToAnalyze.length,
          });

          // Show loading message for subject validation
          Swal.fire({
            title: "🎓 Validating Relevance",
            html: `
          <div style="text-align: left; padding: 20px 5px 10px 5px;">
            <!-- Subject Check Card -->
            <div style="background: white; border: 2px solid black; border-radius: 12px; padding: 18px; margin-bottom: 18px; box-shadow: 4px 4px 0 0 rgba(0,0,0,0.15);">
              <div style="display: flex; align-items: start; gap: 12px;">
                <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%); border-radius: 10px; display: flex; align-items: center; justify-content: center; border: 2px solid black; flex-shrink: 0;">
                  <span style="font-size: 20px;">🎓</span>
                </div>
                <div>
                  <p style="margin: 0 0 6px 0; font-size: 14px; color: #1f2937; font-weight: 700;">
                    Checking content relevance
                  </p>
                  <p style="margin: 0; font-size: 13px; color: #6b7280; font-weight: 500; line-height: 1.4;">
                    Expected Subject: <strong style="color: #1f2937;">${props.sessionContext.subject}</strong>
                  </p>
                </div>
              </div>
            </div>
            
            <!-- Progress Card -->
            <div style="background: white; border: 2px solid black; border-radius: 12px; padding: 16px; margin-bottom: 18px; box-shadow: 4px 4px 0 0 rgba(0,0,0,0.15);">
              <div style="background: #f3f4f6; border-radius: 8px; height: 10px; overflow: hidden; border: 2px solid #e5e7eb; margin-bottom: 12px;">
                <div id="relevance-progress" style="background: linear-gradient(90deg, #f59e0b 0%, #ef4444 100%); height: 100%; width: 0%; transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);"></div>
              </div>
              <div style="display: flex; align-items: center; gap: 10px;">
                <div style="width: 20px; height: 20px; border-radius: 50%; background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%); display: flex; align-items: center; justify-content: center; border: 2px solid black; flex-shrink: 0;">
                  <span style="font-size: 10px;">✓</span>
                </div>
                <span id="relevance-step" style="font-size: 13px; color: #4b5563; font-weight: 600;">🔍 Analyzing content topic...</span>
              </div>
            </div>
          </div>
        `,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            width: "520px",
            customClass: {
              popup: "elevated-modal",
              title: "elevated-modal-title",
            },
            didOpen: () => {
              const progressBar = document.getElementById("relevance-progress");
              const stepText = document.getElementById("relevance-step");

              const steps = [
                { text: "🔍 Analyzing content topic...", progress: 30 },
                { text: "📚 Comparing with session subject...", progress: 60 },
                { text: "📊 Calculating relevance score...", progress: 90 },
              ];

              let currentStep = 0;

              const updateStep = () => {
                if (currentStep < steps.length && progressBar && stepText) {
                  stepText.textContent = steps[currentStep].text;
                  progressBar.style.width = steps[currentStep].progress + "%";
                  currentStep++;
                  setTimeout(updateStep, 1000);
                }
              };

              setTimeout(updateStep, 300);
            },
          });

          const relevanceCheck = await geminiService.validateSubjectRelevance(
            contentToAnalyze,
            props.sessionContext.subject
          );

          logDebug("Subject relevance check completed for pasted text", {
            isRelevant: relevanceCheck.isRelevant,
            relevanceScore: relevanceCheck.relevanceScore,
            detectedSubject: relevanceCheck.detectedSubject,
            expectedSubject: relevanceCheck.expectedSubject,
            confidence: relevanceCheck.confidence,
          });

          // Close validation loading dialog
          Swal.close();

          // Block if relevance score is too low (below 60%)
          if (!relevanceCheck.isRelevant) {
            logWarn(`Pasted text blocked due to subject mismatch`, {
              relevanceScore: relevanceCheck.relevanceScore,
              detectedSubject: relevanceCheck.detectedSubject,
              expectedSubject: relevanceCheck.expectedSubject,
            });

            aiContentStore.setLoading(false);

            await Swal.fire({
              icon: "error",
              title: "Subject Mismatch Detected",
              html: `${relevanceCheck.message}<br><br><strong>Detected Subject:</strong> ${relevanceCheck.detectedSubject}<br><strong>Expected Subject:</strong> ${relevanceCheck.expectedSubject}<br><strong>Relevance Score:</strong> ${relevanceCheck.relevanceScore}%`,
              confirmButtonColor: "#ef4444",
              confirmButtonText: "Update Notes",
            });
            return;
          }

          // Log successful relevance check
          logSuccess(`Subject relevance validated successfully for pasted text`, {
            relevanceScore: relevanceCheck.relevanceScore,
            detectedSubject: relevanceCheck.detectedSubject,
          });

          // ✅ SMOOTH TRANSITION: Wait briefly before continuing validation
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // CRITICAL: Validate language before processing
        logInfo("Validating content language", {
          contentLength: contentToAnalyze.length,
          source: fileName || "text input",
        });

        const languageValidation = await geminiService.validateContentLanguage(contentToAnalyze);

        logDebug("Language validation result", {
          isValid: languageValidation.isValid,
          primaryLanguage: languageValidation.primaryLanguage,
          requiresUserChoice: languageValidation.requiresUserChoice,
          percentages: languageValidation.detectedLanguages,
        });

        // Check for invalid languages
        if (!languageValidation.isValid) {
          logWarn("Invalid language detected", {
            message: languageValidation.message,
          });

          Swal.fire({
            icon: "error",
            title: "Invalid Language Detected",
            html: languageValidation.message,
            confirmButtonColor: "#ef4444",
            confirmButtonText: "OK",
          });
          return;
        }

        // Handle mixed language (Taglish) - require user choice
        let preferredLanguage: "english" | "filipino" | undefined = undefined;

        if (languageValidation.requiresUserChoice) {
          logInfo("Mixed language detected - requesting user preference", {
            english: `${languageValidation.detectedLanguages.english}%`,
            filipino: `${languageValidation.detectedLanguages.filipino}%`,
          });

          const result = await Swal.fire({
            title: "Language Preference",
            html: `
          <div style="text-align: left; padding: 20px 5px 10px 5px;">
            <!-- Detection Message -->
            <div style="background: white; border: 2px solid black; border-radius: 12px; padding: 18px; margin-bottom: 18px; box-shadow: 4px 4px 0 0 rgba(0,0,0,0.15);">
              <div style="display: flex; align-items: start; gap: 12px; margin-bottom: 12px;">
                <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%); border-radius: 10px; display: flex; align-items: center; justify-content: center; border: 2px solid black; flex-shrink: 0;">
                  <span style="font-size: 20px;">🌐</span>
                </div>
                <div>
                  <p style="margin: 0 0 6px 0; font-size: 15px; color: #1f2937; font-weight: 700; line-height: 1.4;">
                    We detected that your notes are in <strong>Taglish</strong>
                  </p>
                  <p style="margin: 0; font-size: 13px; color: #6b7280; font-weight: 500; line-height: 1.5;">
                    Which language would you like the output to use?
                  </p>
                </div>
              </div>
            </div>

            <!-- Tip Box -->
            <div style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border: 2px solid #f59e0b; border-radius: 12px; padding: 14px; box-shadow: 3px 3px 0 0 rgba(245, 158, 11, 0.2);">
              <p style="margin: 0; font-size: 13px; color: #78350f; line-height: 1.5; font-weight: 600;">
                💡 <strong>Tip:</strong> Choose the language that helps you learn best. Your notes stay unchanged - only the AI output will use your selected language.
              </p>
            </div>
          </div>
        `,
            icon: "question",
            iconColor: "#8b5cf6",
            showCancelButton: true,
            confirmButtonColor: "#3b82f6",
            cancelButtonColor: "#10b981",
            confirmButtonText:
              '<div style="display: flex; align-items: center; gap: 8px; justify-content: center;"><span style="font-size: 18px;">🇬🇧</span><span style="font-size: 14px; font-weight: 700;">English</span></div>',
            cancelButtonText:
              '<div style="display: flex; align-items: center; gap: 8px; justify-content: center;"><span style="font-size: 18px;">🇵🇭</span><span style="font-size: 14px; font-weight: 700;">Filipino</span></div>',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showCloseButton: false,
            width: "580px",
            customClass: {
              popup: "language-choice-popup elevated-modal",
              title: "elevated-modal-title",
              confirmButton: "language-btn-confirm",
              cancelButton: "language-btn-cancel",
            },
          });

          if (result.isConfirmed) {
            preferredLanguage = "english";
            logInfo("User chose English output");
          } else if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
            preferredLanguage = "filipino";
            logInfo("User chose Filipino output");
          } else {
            // This should never happen with allowOutsideClick: false and allowEscapeKey: false
            // But as failsafe, default to English and log warning
            logWarn("Language dialog dismissed unexpectedly - defaulting to English");
            preferredLanguage = "english";
          }
        } else {
          // Pure language - use detected language
          if (languageValidation.primaryLanguage === "english") {
            preferredLanguage = "english";
          } else if (languageValidation.primaryLanguage === "filipino") {
            preferredLanguage = "filipino";
          }
          // Don't set preferredLanguage for other cases - let HF handle it

          logInfo("Pure language detected - no user choice needed", {
            language: languageValidation.primaryLanguage,
            english: `${languageValidation.detectedLanguages.english}%`,
            filipino: `${languageValidation.detectedLanguages.filipino}%`,
          });
        }

        // ✅ FIX: Update existing modal to show final generation phase
        // Instead of closing and reopening, update the content in place
        Swal.update({
          html: loadingText, // Reuse the same enhanced loading template
        });

        // Reset the progress animation to restart from the beginning for actual AI generation
        const progressBar = document.getElementById("progress-bar");
        const progressPercentage = document.getElementById("progress-percentage");
        const stepNumber = document.getElementById("current-step-number");
        const stepTitle = document.getElementById("current-step-title");
        const stepDetails = document.getElementById("step-details");

        // Reset to first step
        if (progressBar) progressBar.style.width = "0%";
        if (progressPercentage) progressPercentage.textContent = "0%";

        // Restart the detailed step animation from updateProgress function
        const steps =
          selectedType.value === "summary"
            ? [
                {
                  title: "Content Validation",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>`,
                  progress: 12,
                  details: [
                    "Checking content structure and quality",
                    "Verifying text length and readability",
                    "Ensuring content is suitable for AI processing",
                  ],
                },
                {
                  title: "Duplicate Detection",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>`,
                  progress: 24,
                  details: [
                    "Scanning for previously generated summaries",
                    "Comparing content against existing database",
                    "Ensuring fresh AI generation without duplicates",
                  ],
                },
                {
                  title: "Content Extraction",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>`,
                  progress: 38,
                  details: [
                    "Extracting text from your uploaded file",
                    "Preserving formatting and structure",
                    "Preparing content for AI analysis",
                  ],
                },
                {
                  title: "Concept Identification",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>`,
                  progress: 55,
                  details: [
                    "AI analyzing main topics and themes",
                    "Identifying key concepts and relationships",
                    "Extracting most important information",
                  ],
                },
                {
                  title: "Summary Generation",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>`,
                  progress: 78,
                  details: [
                    "Gemini 2.5-Pro AI crafting your summary",
                    "Synthesizing information into clear sections",
                    "Ensuring comprehensive yet concise output",
                  ],
                },
                {
                  title: "Quality Assurance",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>`,
                  progress: 100,
                  details: [
                    "Validating summary accuracy and completeness",
                    "Formatting content for readability",
                    "Final quality checks before delivery",
                  ],
                },
              ]
            : [
                {
                  title: "Content Validation",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>`,
                  progress: 10,
                  details: [
                    "Checking content structure and quality",
                    "Verifying sufficient material for flashcards",
                    "Ensuring content meets AI requirements",
                  ],
                },
                {
                  title: "Duplicate Detection",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>`,
                  progress: 20,
                  details: [
                    "Scanning for previously generated flashcards",
                    "Comparing content against existing database",
                    "Ensuring unique flashcard generation",
                  ],
                },
                {
                  title: "Content Extraction",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>`,
                  progress: 32,
                  details: [
                    "Extracting text from your uploaded file",
                    "Identifying key facts and concepts",
                    "Preparing content for AI processing",
                  ],
                },
                {
                  title: "Learning Point Identification",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>`,
                  progress: 50,
                  details: [
                    "AI analyzing content for testable concepts",
                    "Identifying 20 most important learning points",
                    "Determining optimal question difficulty",
                  ],
                },
                {
                  title: "Question Generation",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>`,
                  progress: 70,
                  details: [
                    "Gemini 2.5-Flash AI crafting questions",
                    "Creating diverse question types",
                    "Ensuring questions test understanding",
                  ],
                },
                {
                  title: "Answer Creation",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>`,
                  progress: 88,
                  details: [
                    "Generating accurate and detailed answers",
                    "Adding helpful explanations",
                    "Verifying answer correctness",
                  ],
                },
                {
                  title: "Quality Assurance",
                  icon: `<svg style="width: 14px; height: 14px; color: #8b5cf6;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>`,
                  progress: 100,
                  details: [
                    "Validating all 20 flashcards for quality",
                    "Ensuring variety and proper coverage",
                    "Final formatting and preparation",
                  ],
                },
              ];

        let currentStepIndex = 0;

        const restartProgress = () => {
          if (currentStepIndex < steps.length) {
            const step = steps[currentStepIndex];

            // Update progress bar and percentage
            if (progressBar) progressBar.style.width = step.progress + "%";
            if (progressPercentage) progressPercentage.textContent = step.progress + "%";

            // Update step number
            if (stepNumber)
              stepNumber.textContent = `Step ${currentStepIndex + 1} of ${steps.length}`;

            // Update step title with icon
            if (stepTitle) {
              stepTitle.innerHTML = `
                <span id="step-icon" style="display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: white; border-radius: 6px; border: 2px solid #8b5cf6;">
                  ${step.icon}
                </span>
                ${step.title}
              `;
            }

            // Update step details with animations
            if (stepDetails) {
              stepDetails.innerHTML = step.details
                .map(
                  (detail, index) => `
                <div style="display: flex; align-items: start; gap: 8px; margin-bottom: 6px; animation: ai-fade-in 0.5s ease-out ${
                  index * 0.1
                }s both;">
                  <span style="display: inline-block; width: 6px; height: 6px; background: #8b5cf6; border-radius: 50%; margin-top: 6px; flex-shrink: 0; animation: ai-pulse 2s ease-in-out infinite;"></span>
                  <span style="font-size: 12px; color: #4c1d95; font-weight: 500; line-height: 1.5;">${detail}</span>
                </div>
              `
                )
                .join("");
            }

            currentStepIndex++;

            // Variable timing: earlier steps faster, later steps (AI generation) slower
            const baseDelay = currentStepIndex < 3 ? 3000 : currentStepIndex < 5 ? 5000 : 7000;
            const randomVariation = Math.random() * 2000;
            setTimeout(restartProgress, baseDelay + randomVariation);
          }
        };

        // Start fresh progress animation
        setTimeout(restartProgress, 500);

        // Continue with content generation using preferredLanguage
        // Determine source type for Firebase storage
        let sourceType: "pdf" | "txt" | "docx" | "note" = "note";
        if (uploadedFiles.value.length > 0) {
          const fileExtension = uploadedFiles.value[0].name.toLowerCase().split(".").pop();
          if (fileExtension === "pdf") sourceType = "pdf";
          else if (fileExtension === "txt") sourceType = "txt";
          else if (fileExtension === "docx") sourceType = "docx";
        }

        // ✅ RETRY MECHANISM: Try up to MAX_RETRIES times with countdown between failures
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            await attemptGenerateContent(
              attempt,
              MAX_RETRIES,
              contentToAnalyze,
              fileName,
              sourceType,
              preferredLanguage
            );

            // ✅ Success - reset failed attempts and break out of retry loop
            resetFailedAttempts();
            break;
          } catch (error) {
            // Record this failed attempt
            recordFailedGenerationAttempt();

            logError(`Generation attempt ${attempt}/${MAX_RETRIES} failed:`, error);

            // Check if we've exhausted all retries
            if (attempt >= MAX_RETRIES) {
              // ✅ Final failure (3/3) - show Close-only modal
              logError("All generation attempts exhausted", {
                totalAttempts: attempt,
                maxRetries: MAX_RETRIES,
              });

              // Close loading dialog first
              Swal.close();

              await Swal.fire({
                icon: "error",
                title: `Attempt ${attempt}/${MAX_RETRIES} Failed`,
                html: `
                  <div style="text-align: left; padding: 12px;">
                    <p style="color: #dc2626; font-weight: 600; margin-bottom: 12px;">
                      ❌ ${
                        selectedType.value === "summary" ? "Summary" : "Flashcard"
                      } generation failed after ${MAX_RETRIES} attempts.
                    </p>
                    <p style="color: #6b7280; font-size: 14px; margin-bottom: 8px;">
                      This may be due to:
                    </p>
                    <ul style="color: #4b5563; font-size: 13px; margin-left: 20px; margin-bottom: 12px;">
                      <li>AI service overload or temporary issues</li>
                      <li>Network connectivity problems</li>
                      <li>Complex content requiring simplification</li>
                    </ul>
                    <p style="color: #4b5563; font-size: 13px;">
                      Please try again later or contact support if the issue persists.
                    </p>
                  </div>
                `,
                showConfirmButton: false,
                cancelButtonText: "Close",
                showCancelButton: true,
                cancelButtonColor: "#6b7280",
                allowOutsideClick: false,
              });

              // Don't throw - just return to allow user to try again
              return;
            }

            // ✅ Not final attempt - show retry modal with countdown
            const remainingAttempts = MAX_RETRIES - attempt;

            // Calculate exponential backoff delay (2s, 4s, 8s)
            const delays = [0, 2000, 4000, 8000]; // Index 0 unused, attempts 1-3
            const delayMs = delays[attempt] || 8000;
            const delaySec = Math.floor(delayMs / 1000);

            logWarn(`Showing retry modal for attempt ${attempt}/${MAX_RETRIES}`, {
              remainingAttempts,
              delaySeconds: delaySec,
            });

            // Close loading dialog before showing retry modal
            Swal.close();

            // Show retry modal with countdown
            const result = await Swal.fire({
              icon: "warning",
              title: `Attempt ${attempt}/${MAX_RETRIES} Failed`,
              html: `
                <div style="text-align: left; padding: 12px;">
                  <p style="color: #dc2626; font-weight: 600; margin-bottom: 12px;">
                    ⚠️ ${
                      selectedType.value === "summary" ? "Summary" : "Flashcard"
                    } generation failed.
                  </p>
                  <p style="color: #6b7280; font-size: 14px; margin-bottom: 8px;">
                    You have <strong>${remainingAttempts} ${
                remainingAttempts === 1 ? "attempt" : "attempts"
              } remaining</strong>.
                  </p>
                  <p style="color: #4b5563; font-size: 13px; margin-bottom: 12px;">
                    Retrying in <strong id="countdown-timer">${delaySec}</strong> seconds...
                  </p>
                  <p style="color: #6b7280; font-size: 12px;">
                    This helps avoid overloading the AI service.
                  </p>
                </div>
              `,
              showConfirmButton: true,
              confirmButtonText: `Retry Now (${remainingAttempts} left)`,
              confirmButtonColor: "#2563eb",
              showCancelButton: true,
              cancelButtonText: "Cancel",
              cancelButtonColor: "#6b7280",
              allowOutsideClick: false,
              didOpen: () => {
                const countdownEl = document.getElementById("countdown-timer");
                let timeLeft = delaySec;

                const countdownInterval = setInterval(() => {
                  timeLeft--;
                  if (countdownEl) {
                    countdownEl.textContent = timeLeft.toString();
                  }

                  if (timeLeft <= 0) {
                    clearInterval(countdownInterval);
                    // Auto-confirm after countdown
                    Swal.clickConfirm();
                  }
                }, 1000);
              },
            });

            // User cancelled retry
            if (result.isDismissed || result.dismiss === Swal.DismissReason.cancel) {
              logInfo("User cancelled retry attempt");
              return; // Exit retry loop
            }

            // User clicked "Retry Now" or countdown finished - continue to next attempt
            logInfo(`Retrying generation (attempt ${attempt + 1}/${MAX_RETRIES})...`);

            // Re-show loading dialog for next attempt
            Swal.fire({
              title: loadingTitle,
              html: loadingText,
              allowOutsideClick: false,
              allowEscapeKey: false,
              showConfirmButton: false,
              didOpen: () => {
                Swal.showLoading();
              },
            });

            // Restart progress animation
            setTimeout(restartProgress, 500);
          }
        }
      } catch (outerError) {
        // This catches errors from the retry mechanism itself (not generation failures)
        logError("Critical error in retry mechanism:", outerError);
        Swal.close();

        Swal.fire({
          icon: "error",
          title: "Unexpected Error",
          text: "An unexpected error occurred. Please try again later.",
          confirmButtonColor: "#ef4444",
        });
      }
    },
  });
};

/**
 * ✅ Actual generation logic extracted into separate function for retry mechanism
 * This function throws on failure to trigger retry logic
 */
async function attemptGenerateContent(
  attempt: number,
  maxRetries: number,
  contentToAnalyze: string,
  fileName: string,
  sourceType: "pdf" | "txt" | "docx" | "note",
  preferredLanguage: "english" | "filipino" | undefined
) {
  // Generate content based on selected type
  if (selectedType.value === "summary") {
    logInfo("Starting enhanced summary generation", {
      contentLength: contentToAnalyze.length,
      fileName,
      sourceType,
      preferredLanguage: preferredLanguage || "auto-detected",
      sessionSubject: props.sessionContext?.subject,
    });

    // Generate summary using enhanced pipeline with language preference
    // ✅ Pass session subject directly (skips AI subject detection)
    const summaryResult = await geminiService.generateEnhancedSummaryWithHF(
      contentToAnalyze,
      fileName,
      preferredLanguage,
      props.sessionContext?.subject // Use subject from completed session
    );

    // Store the generated content (this will automatically save to Firebase)
    // Pass the uploaded file object if available
    const uploadedFile = uploadedFiles.value.length > 0 ? uploadedFiles.value[0] : null;

    console.log("🔄 STARTING FIREBASE SAVE for Summary...");
    await aiContentStore.setGeneratedContent(
      summaryResult,
      fileName,
      contentToAnalyze,
      sourceType,
      uploadedFile,
      props.sessionContext?.sessionId // Pass session ID from completed session
    );
    console.log("✅ FIREBASE SAVE COMPLETED for Summary - ID:", aiContentStore.savedSummaryId);

    // Close loading dialog
    Swal.close();

    // Show success message with Firebase save status
    const saveStatus = aiContentStore.savedSummaryId
      ? `Your AI-powered summary has been created and saved to your library.`
      : "Your AI-powered summary has been created but couldn't be saved to your library. You can still view and export it.";

    console.log("📊 Firebase Save Status:", {
      savedSummaryId: aiContentStore.savedSummaryId,
      topic: summaryResult.topic,
      subject: summaryResult.subject,
      timestamp: new Date().toISOString(),
    });

    await Swal.fire({
      icon: "success",
      title: "Summary Generated Successfully!",
      text: saveStatus,
      confirmButtonColor: "#10b981",
      confirmButtonText: "View Summary",
      timer: 6000,
      timerProgressBar: true,
    });

    logSuccess("Summary generation completed successfully");

    console.log("🚀 Navigating to summary view page...");
    // Navigate to the AI-Generated Study Material page (validator or student)
    const userIsValidator = await isValidator();
    const summaryRoute = userIsValidator
      ? "/validator/ai-generated-study-material"
      : "/student/ai-generated-study-material";
    router.push(summaryRoute);
  } else if (selectedType.value === "flashcards") {
    logInfo("Starting flashcard generation", {
      contentLength: contentToAnalyze.length,
      fileName,
      sourceType,
      model: "gemini-2.5-flash",
      preferredLanguage: preferredLanguage || "auto-detected",
      sessionSubject: props.sessionContext?.subject,
    });

    // Generate flashcards using Gemini 2.5-flash with language preference
    // ✅ Pass session subject directly (skips AI subject detection)
    const flashcardResult = await geminiService.generateFlashcards(
      contentToAnalyze,
      fileName,
      10, // ✅ ALWAYS generate 10 flashcards from user's uploaded content
      props.sessionContext?.subject, // Use subject from completed session
      preferredLanguage // Language preference moved to last parameter
    );

    // Store the generated flashcards (this will automatically save to Firebase)
    // Pass the uploaded file object if available
    const uploadedFileFlashcard = uploadedFiles.value.length > 0 ? uploadedFiles.value[0] : null;

    console.log("🔄 STARTING FIREBASE SAVE for Flashcards...");
    await aiContentStore.setGeneratedFlashcards(
      flashcardResult,
      fileName,
      contentToAnalyze,
      sourceType,
      uploadedFileFlashcard,
      props.sessionContext?.sessionId // Pass session ID from completed session
    );
    console.log(
      "✅ FIREBASE SAVE COMPLETED for Flashcards - ID:",
      aiContentStore.savedFlashcardsId
    );

    // Close loading dialog
    Swal.close();

    // Show success message with Firebase save status
    const saveStatus = aiContentStore.savedFlashcardsId
      ? `Your ${flashcardResult.flashcards.length} AI-powered flashcards have been created and saved to your library.`
      : `Your ${flashcardResult.flashcards.length} AI-powered flashcards have been created but couldn't be saved to your library. You can still view and export them.`;

    console.log("📊 Firebase Save Status:", {
      savedFlashcardsId: aiContentStore.savedFlashcardsId,
      cardCount: flashcardResult.flashcards.length,
      topic: flashcardResult.topic,
      subject: flashcardResult.subject,
      timestamp: new Date().toISOString(),
    });

    await Swal.fire({
      icon: "success",
      title: "Flashcards Generated Successfully!",
      text: saveStatus,
      confirmButtonColor: "#10b981",
      confirmButtonText: "View Flashcards",
      timer: 6000,
      timerProgressBar: true,
    });

    logSuccess("Flashcard generation completed successfully", {
      flashcardCount: flashcardResult.flashcards.length,
      topic: flashcardResult.topic,
      subject: flashcardResult.subject,
    });

    console.log("🚀 Navigating to flashcards view page...");
    // Navigate to the AI-Generated Flashcards page (validator or student)
    const userIsValidator = await isValidator();
    const flashcardsRoute = userIsValidator
      ? "/validator/ai-generated-flashcards"
      : "/student/ai-generated-flashcards";
    router.push(flashcardsRoute);
  } else {
    throw new Error(`Unsupported content type: ${selectedType.value}`);
  }
}
</script>

<style scoped>
/* Fix SweetAlert2 modal background shift - prevent body padding when modal opens */
:deep(body.swal2-shown:not(.swal2-no-backdrop):not(.swal2-toast-shown)) {
  padding-right: 0 !important;
}

:deep(.swal2-container) {
  padding-right: 0 !important;
}

/* Enhanced Elevated Modal Design System */
:deep(.elevated-modal) {
  border-radius: 16px !important;
  background: #ffffff !important;
  border: 3px solid #000000 !important;
  box-shadow: 6px 6px 0 0 rgba(0, 0, 0, 0.25) !important;
  padding: 24px !important;
}

:deep(.elevated-modal-title) {
  color: #1f2937 !important;
  font-weight: 800 !important;
  font-size: 1.5rem !important;
  margin-bottom: 20px !important;
  letter-spacing: -0.5px !important;
}

:deep(.elevated-modal .swal2-html-container) {
  margin: 0 !important;
  padding: 0 !important;
  overflow: visible !important;
}

/* Custom styles for AI Disclaimer popup */
:deep(.ai-disclaimer-popup) {
  border-radius: 16px !important;
  background: linear-gradient(145deg, #ffffff 0%, #fefbf3 100%) !important;
  box-shadow: 0 20px 40px rgba(245, 158, 11, 0.2), 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

:deep(.ai-disclaimer-title) {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  background-clip: text !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 700 !important;
  font-size: 1.4rem !important;
  margin-bottom: 10px !important;
}

:deep(.ai-disclaimer-content) {
  padding: 0 10px !important;
}

:deep(.ai-disclaimer-button) {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  border: none !important;
  border-radius: 10px !important;
  padding: 12px 30px !important;
  font-weight: 600 !important;
  font-size: 1rem !important;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4) !important;
  transition: all 0.3s ease !important;
}

:deep(.ai-disclaimer-button:hover) {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%) !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 16px rgba(245, 158, 11, 0.5) !important;
}

:deep(.ai-disclaimer-button:focus) {
  outline: none !important;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.3), 0 6px 16px rgba(245, 158, 11, 0.5) !important;
}

/* Enhanced language button styles with elevated design */
:deep(.language-btn-confirm),
:deep(.language-btn-cancel) {
  padding: 14px 28px !important;
  border-radius: 10px !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  border: 2px solid black !important;
  transition: all 0.2s ease !important;
  box-shadow: 3px 3px 0 0 rgba(0, 0, 0, 0.2) !important;
}

:deep(.language-btn-confirm) {
  background: #3b82f6 !important;
  color: white !important;
}

:deep(.language-btn-cancel) {
  background: #10b981 !important;
  color: white !important;
}

:deep(.language-btn-confirm:hover) {
  transform: translate(-2px, -2px) !important;
  box-shadow: 5px 5px 0 0 rgba(0, 0, 0, 0.25) !important;
  background: #2563eb !important;
}

:deep(.language-btn-cancel:hover) {
  transform: translate(-2px, -2px) !important;
  box-shadow: 5px 5px 0 0 rgba(0, 0, 0, 0.25) !important;
  background: #059669 !important;
}

:deep(.language-btn-confirm:active),
:deep(.language-btn-cancel:active) {
  transform: translate(0, 0) !important;
  box-shadow: 2px 2px 0 0 rgba(0, 0, 0, 0.2) !important;
}

/* Subtle floating animation for AI Content Notice */
@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
}

.floating-notice {
  animation: float 3s ease-in-out infinite;
}

/* Hover effect - pause animation and slightly scale up */
.floating-notice:hover {
  animation-play-state: paused;
  transform: scale(1.02) translateY(0px) !important;
  transition: transform 0.2s ease;
}
</style>
